import { 
  Hospital, 
  Siren,   
  Flame, 
  Ambulance, 
  Mountain,   
  Waves,    
  Brain      
} from 'lucide-react';

import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { Home, Edit, Menu, User, ChevronRight, MapPin, Phone, Video, Camera, Image, AlertCircle, Navigation, Heart, Cloud, CloudRain, Wind, Thermometer, Activity, Wifi, WifiOff, Radio, Users,PlusCircle, ChevronLeft, Upload, Trash } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Firebase (inlined minimal setup)
import { initializeApp } from "firebase/app";
import { getAuth, signInAnonymously } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

// imports...

import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';


import React, { useState, useEffect, useCallback, useRef } from 'react';
// Firestore imports MUST BE AT TOP
import {
  getFirestore,
  collection,
  addDoc,
  setDoc,
  doc,
  updateDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  arrayUnion, deleteDoc,
  query,
  orderBy,
  where,
} from "firebase/firestore";

async function requestMobilePermissions() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const locPerm = await Geolocation.requestPermissions();
    console.log("Loc perm: ", locPerm);

    const notifPerm = await PushNotifications.requestPermissions();
    if (notifPerm.receive === "granted") {
      PushNotifications.register();
    }
  } catch (err) {
    console.log("Permission request failed", err);
  }
}

async function getCurrentPositionSafe() {
  if (!Capacitor.isNativePlatform()) {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    });
  } else {
    await Geolocation.checkPermissions(); // keep permission active
    return await Geolocation.getCurrentPosition();
  }
}

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const WEATHER_API_KEY = process.env.REACT_APP_WEATHER_API_KEY;
const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY;

console.log('Google Maps API Key:', GOOGLE_MAPS_API_KEY ? 'Loaded ✓' : 'Missing ✗');
console.log('Weather API Key:', WEATHER_API_KEY ? 'Loaded ✓' : 'Missing ✗');
console.log('Geoapify API Key:', GEOAPIFY_API_KEY ? 'Loaded ✓' : 'Missing ✗');

const fetchRoute = async (startLat, startLng, endLat, endLng) => {
  try {
    if (!GOOGLE_MAPS_API_KEY) {
      console.warn('No Google Maps API key');
      return { path: [[startLat, startLng], [endLat, endLng]], steps: [], distanceKm: null };
    }

    if (!window.google || !window.google.maps) {
      console.warn("Google Maps not loaded yet");
      return { path: [[startLat, startLng], [endLat, endLng]], steps: [], distanceKm: null };
    }

    const directionsService = new window.google.maps.DirectionsService();

    return new Promise((resolve) => {
      directionsService.route(
        {
          origin: { lat: startLat, lng: startLng },
          destination: { lat: endLat, lng: endLng },
          travelMode: window.google.maps.TravelMode.DRIVING
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            const route = result.routes[0];
            const path = route.overview_path.map(p => [p.lat(), p.lng()]);
            
            const distanceMeters = route.legs?.[0]?.distance?.value || null;
            const distanceKm = distanceMeters ? distanceMeters / 1000 : null;

            // Extract turn-by-turn steps
            const steps = route.legs[0].steps.map((step, idx) => ({
              instruction: step.instructions.replace(/<[^>]*>/g, ''), // Remove HTML tags
              distance: step.distance.text,
              distanceMeters: step.distance.value,
              duration: step.duration.text,
              maneuver: step.maneuver || 'straight',
              startLocation: { lat: step.start_location.lat(), lng: step.start_location.lng() },
              endLocation: { lat: step.end_location.lat(), lng: step.end_location.lng() }
            }));

            resolve({ path, steps, distanceKm });
          } else {
            console.warn('Directions API failed:', status);
            resolve({ path: [[startLat, startLng], [endLat, endLng]], steps: [], distanceKm: null });
          }
        }
      );
    });
  } catch (error) {
    console.error('Route fetch failed:', error);
    return { path: [[startLat, startLng], [endLat, endLng]], steps: [], distanceKm: null };
  }
};
const enrichStepWithLandmarks = async (step) => {
  try {
    if (!window.google || !GOOGLE_MAPS_API_KEY) return step;

    const { startLocation } = step;
    const mapDiv = document.createElement("div");
    const map = new window.google.maps.Map(mapDiv);
    const service = new window.google.maps.places.PlacesService(map);

    return new Promise((resolve) => {
      service.nearbySearch(
        {
          location: startLocation,
          radius: 100,
          type: ['point_of_interest', 'establishment']
        },
        (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
            const landmark = results[0].name;
            let enrichedInstruction = step.instruction;
            
            // Replace generic directions with landmark-based ones
            if (step.maneuver === 'turn-right') {
              enrichedInstruction = `Turn right next to ${landmark}`;
            } else if (step.maneuver === 'turn-left') {
              enrichedInstruction = `Turn left near ${landmark}`;
            } else if (step.instruction.toLowerCase().includes('straight')) {
              enrichedInstruction = `Continue straight past ${landmark}`;
            } else {
              enrichedInstruction = `${step.instruction} (near ${landmark})`;
            }
            
            resolve({ ...step, instruction: enrichedInstruction, landmark });
          } else {
            resolve(step);
          }
        }
      );
    });
  } catch (err) {
    console.error("Landmark enrichment failed:", err);
    return step;
  }
};
const fetchExactAddress = async (lat, lng) => {
  try {
    if (!GOOGLE_MAPS_API_KEY) return null;
    
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`;
    const res = await fetch(url);
    const data = await res.json();

    if (data.status === 'OK' && data.results && data.results[0]) {
      return data.results[0].formatted_address;
    }
    return null;
  } catch (err) {
    console.error("Reverse geocoding failed:", err);
    return null;
  }
};


// Custom marker icons
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
};

const App = () => {
    React.useEffect(() => {
    if (GOOGLE_MAPS_API_KEY && !window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);
  const [currentScreen, setCurrentScreen] = useState('splash');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('action');
  const [userLocation, setUserLocation] = useState({ lat: 12.9712, lng: 77.5946 });
  const [weather, setWeather] = useState(null);
  const [weatherAlerts, setWeatherAlerts] = useState([]);
  const [meshStatus, setMeshStatus] = useState('disconnected');
  const [meshPeers, setMeshPeers] = useState([]);
  const [meshMessages, setMeshMessages] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [meshNode, setMeshNode] = useState(null);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [locationPermission, setLocationPermission] = useState('prompt');
  const [showEmergencyCallMenu, setShowEmergencyCallMenu] = useState(false);
  const [selectedEmergencyType, setSelectedEmergencyType] = useState(null);
  const [createdEvents, setCreatedEvents] = useState([]);
  const [eventVolunteers, setEventVolunteers] = useState({});
  const [userRespondingTo, setUserRespondingTo] = useState([]);
  const [userActivities, setUserActivities] = useState([]);
  const [eventMediaFiles, setEventMediaFiles] = useState([]);
  const [showEventChat, setShowEventChat] = useState(false);
  const [chatMessages, setChatMessages] = useState({});
  const [eventMessages, setEventMessages] = useState([]);
  const [isChatMaximized, setIsChatMaximized] = useState(true);
  
  const [emergencyRoutes, setEmergencyRoutes] = useState([]);
  const [showEmergencyRoutes, setShowEmergencyRoutes] = useState(false);
  const typingTimeoutRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  // Resource filter for Nearby Resources screen (All / Healthcare / Police / Fire / Pharmacy)
  const [resourceFilter, setResourceFilter] = useState('all');

  const [activeEventTab, setActiveEventTab] = useState('updates');
  const [eventUpdates, setEventUpdates] = useState({});
  const [newUpdateText, setNewUpdateText] = useState('');
  const [showAddUpdateDialog, setShowAddUpdateDialog] = useState(false);

 // Chat/media/recording state (add near your existing chat state block)
const [recording, setRecording] = useState(false);
const mediaRecorderRef = useRef(null);
const recordedChunksRef = useRef([]);
const chatFileInputRef = useRef(null); // hidden file input for chat uploads

const [nearbyResources, setNearbyResources] = useState([]);
const [selectedResource, setSelectedResource] = useState(null);

const deleteMediaItem = async (eventId, mediaObj) => {
  if (!mediaObj) return;

  try {
    // 1) Storage delete
    if (mediaObj.path) {
      try {
        await deleteObject(ref(storage, mediaObj.path));
      } catch (err) {
        console.warn("Storage delete failed:", err);
      }
    }

    // 2) Update event mediaFiles
    const prev = createdEvents.find(e => e.id === eventId);
    const updated = (prev.mediaFiles || []).filter(m => m.path !== mediaObj.path);

    try {
      await updateDoc(doc(db, "createdEvents", eventId), { mediaFiles: updated });
    } catch (e) {
      console.warn("Firestore update failed:", e);
    }

    setCreatedEvents(prev =>
      prev.map(ev => ev.id === eventId ? { ...ev, mediaFiles: updated } : ev)
    );

    if (selectedEvent?.id === eventId) {
      setSelectedEvent(prev => ({ ...prev, mediaFiles: updated }));
    }

    // 3) Delete chat entries referencing this media
    try {
      const chatCol = collection(db, "createdEvents", eventId, "chat");
      const q = query(chatCol, where("media.path", "==", mediaObj.path));
      const snaps = await getDocs(q);
      for (const sd of snaps.docs) {
        try { await deleteDoc(sd.ref); } catch (err) {}
      }
    } catch (err) {
      console.warn("Chat cleanup failed:", err);
    }

  } catch (err) {
    console.error("deleteMediaItem failed:", err);
  }
};

useEffect(() => {
  if (!selectedEvent?.id) return;

  const chatColRef = collection(db, "createdEvents", selectedEvent.id, "chat");
  const q = query(chatColRef, orderBy("timestamp", "asc"));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const msgs = snapshot.docs.map(doc => {
      const d = doc.data();
      return {
        ...d,
        timestamp: d.timestamp?.toDate ? d.timestamp.toDate() : d.timestamp
      };
    });

    setChatMessages(prev => ({ ...prev, [selectedEvent.id]: msgs }));
    setEventMessages(msgs);
  });

  return () => unsubscribe();
}, [selectedEvent?.id]);

useEffect(() => {
  if (!selectedEvent?.id) return;

  const updatesRef = collection(db, "createdEvents", selectedEvent.id, "updates");
  const q = query(updatesRef, orderBy("timestamp", "desc"));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const updates = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate ? doc.data().timestamp.toDate() : doc.data().timestamp
    }));

    setEventUpdates(prev => ({ ...prev, [selectedEvent.id]: updates }));
  });

  return () => unsubscribe();
}, [selectedEvent?.id]);


useEffect(() => {
  if (!selectedEvent || selectedEvent === null) return;
  if (selectedEvent.exactAddress) return;
  if (typeof selectedEvent.lat !== "number" || typeof selectedEvent.lng !== "number") return;

  fetchExactAddress(selectedEvent.lat, selectedEvent.lng)
    .then(addr => {
      if (addr) {
        setSelectedEvent(prev =>
          prev ? { ...prev, exactAddress: addr } : prev
        );
      }
    })
    .catch(err => console.error("Reverse geocode error:", err));
}, [selectedEvent]);


  const [currentChatMessage, setCurrentChatMessage] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [showMediaDropdown, setShowMediaDropdown] = useState(false);
  const [newEventForm, setNewEventForm] = useState({
  incidentType: '',
  location: '',
  volunteersNeeded: 1,
  suppliesNeeded: '',
  emergencyServiceStatus: 'Not Arrived',
  mediaFiles: []
});
const [showCallEndDialog, setShowCallEndDialog] = useState(false);
const [pendingTimerCall, setPendingTimerCall] = useState(null);
const [routeCoordinates, setRouteCoordinates] = useState([]);
const [showFullscreenMedia, setShowFullscreenMedia] = useState(false);
const [fullscreenMediaIndex, setFullscreenMediaIndex] = useState(0);
const [reqName, setReqName] = useState("");
const [reqContact, setReqContact] = useState("");
const [reqDescription, setReqDescription] = useState("");
const [requests, setRequests] = useState([]);
const [showReqDropdown, setShowReqDropdown] = useState(false);
const [navigationSteps, setNavigationSteps] = useState([]);
const [currentStepIndex, setCurrentStepIndex] = useState(0);
const [showNavigationPanel, setShowNavigationPanel] = useState(false);
const [userHeading, setUserHeading] = useState(0);
const isMobile = /android|iphone|ipad|mobile|miui|oppo|vivo|oneplus|realme/i.test(
  navigator.userAgent
);
// Context menu state for active events list (right-click)
const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, event: null });

const handleContextMenu = (e, event) => {
  e.preventDefault();
  setContextMenu({
    visible: true,
    x: e.clientX,
    y: e.clientY,
    event: event
  });
};

const hideContextMenu = () => setContextMenu({ visible: false, x: 0, y: 0, event: null });

const sendTextMessage = async (eventId) => {
  if (!currentChatMessage.trim()) return;
  const message = {
    sender: auth.currentUser?.displayName || 'Anonymous',
    userId: auth.currentUser?.uid || 'anonymous',
    text: currentChatMessage.trim(),
    timestamp: serverTimestamp(),
    isVolunteer: userRespondingTo.includes(eventId),
    type: 'text'
  };

  const chatColRef = collection(db, "createdEvents", eventId, "chat");
  try {
    await addDoc(chatColRef, message);
  } catch (err) {
    console.error("Failed to send chat message:", err);
  }

  setCurrentChatMessage('');
};
	
const toggleMessageAsUpdate = async (eventId, messageId, message) => {
  if (!messageId) {
    console.error('Message ID is undefined');
    return;
  }

  try {
    const updateRef = doc(db, "createdEvents", eventId, "updates", messageId);
    const isStarred = (eventUpdates[eventId] || []).some(u => u.id === messageId);
    
    if (isStarred) {
      // Remove from updates
      await deleteDoc(updateRef);
    } else {
      // Add to updates with proper timestamp handling
      await setDoc(updateRef, {
        text: message.text,
        sender: message.sender,
        timestamp: message.timestamp instanceof Date 
          ? message.timestamp 
          : serverTimestamp(),
        type: 'chat',
        messageId: messageId
      });
    }
  } catch (err) {
    console.error("Failed to toggle update:", err);
    alert("Failed to update. Check console.");
  }
};

const addManualUpdate = async (eventId) => {
  if (!newUpdateText.trim()) return;
  
  try {
    const updateRef = collection(db, "createdEvents", eventId, "updates");
    await addDoc(updateRef, {
      text: newUpdateText.trim(),
      sender: auth.currentUser?.displayName || 'Event Creator',
      timestamp: serverTimestamp(),
      type: 'manual'
    });
    
    setNewUpdateText('');
    setShowAddUpdateDialog(false);
  } catch (err) {
    console.error("Failed to add manual update:", err);
  }
};

/**
 * Upload media and sync it to:
 * 1) Firebase Storage
 * 2) Chat messages array
 * 3) eventDetails.mediaFiles
 * 4) Local UI states (media tab + selectedEvent)
 */
const uploadAndSendMedia = async (fileOrBlob, type = "image") => {
  if (!selectedEvent?.id) {
    console.warn("No event selected for media upload");
    return;
  }

  try {
    // Upload file
    const uploaded = await uploadFile(fileOrBlob);
    if (!uploaded) return;

    // Build media entry
    const mediaEntry = {
      type,
      url: uploaded.url,
      path: uploaded.path,
      uploadedAt: Date.now(),
      userId: auth.currentUser?.uid || "anonymous",
    };

    // Update Firestore (createdEvents only)
    await updateDoc(doc(db, "createdEvents", selectedEvent.id), {
      mediaFiles: arrayUnion(mediaEntry),
    });

    // Update React state (Media tab)
    setSelectedEvent(prev => ({
      ...prev,
      mediaFiles: [...(prev.mediaFiles || []), mediaEntry]
    }));

    setCreatedEvents(prev =>
      prev.map(ev =>
        ev.id === selectedEvent.id
          ? { ...ev, mediaFiles: [...(ev.mediaFiles || []), mediaEntry] }
          : ev
      )
    );

    //  Also insert into chat (optional but you wanted it)
    const chatRef = collection(db, "createdEvents", selectedEvent.id, "chat");
    await addDoc(chatRef, {
      sender: "You",
      userId: auth.currentUser?.uid,
      type,
      media: mediaEntry,
      timestamp: serverTimestamp(),
    });

  } catch (err) {
    console.error("Media upload failed:", err);
  }
};
// Replace your existing deleteCreatedEvent function with this
const deleteCreatedEvent = async (id) => {
  if (!id) return setShowDeleteConfirm(false);

  try {
    // find event in createdEvents or selectedEvent
    const ev = createdEvents.find(e => e.id === id) || (selectedEvent && selectedEvent.id === id ? selectedEvent : null);
    const city = ev?.cityName || ev?.city || "unknown-city";

    // 1) delete storage files (best effort)
    if (ev?.mediaFiles && Array.isArray(ev.mediaFiles)) {
      await Promise.all(ev.mediaFiles.map(async (m) => {
        if (m?.path) {
          try { await deleteObject(ref(storage, m.path)); } catch (e) { console.warn("storage delete failed", e); }
        }
      }));
    }

    // 2) delete chat doc and eventDetails doc
    try {
      await deleteDoc(doc(db, "events", city, id, "chat"));
    } catch (e) { console.warn("Failed to delete chat doc:", e); }

    try {
      await deleteDoc(doc(db, "events", city, id, "eventDetails"));
    } catch (e) { console.warn("Failed to delete eventDetails doc", e); }

    // 3) delete createdEvents index doc if exists
    try { await deleteDoc(doc(db, "createdEvents", id)); } catch (e) { /* ignore */ }

    // 4) update local state
    setCreatedEvents(prev => prev.filter(ev => ev.id !== id));
    if (selectedEvent?.id === id) setSelectedEvent(null);
    setShowDeleteConfirm(false);
    setEventToDelete(null);
    hideContextMenu();
    setCurrentScreen('createdEvents');

    console.log("Event fully removed:", id);
  } catch (err) {
    console.error("Delete failed:", err);
    setShowDeleteConfirm(false);
    setEventToDelete(null);
  }
};
const startEditEvent = (event) => {
  // Put the selected event into the create form and open create screen
  setSelectedEvent(event);
  setNewEventForm(prev => ({
    ...prev,
    incidentType: event.type || prev.incidentType,
    location: event.location || prev.location,
    volunteersNeeded: event.volunteersNeeded || prev.volunteersNeeded,
    suppliesNeeded: event.suppliesNeeded || prev.suppliesNeeded,
    emergencyServiceStatus: event.emergencyServiceStatus || prev.emergencyServiceStatus,
    mediaFiles: event.mediaFiles || []
  }));
  setCurrentScreen('createEvent');
  hideContextMenu();
};

const updateEvent = () => {
  if (!editingEvent) return;
  
  setCreatedEvents(createdEvents.map(e => 
    e.id === editingEvent.id ? editingEvent : e
  ));
  
  setEditingEvent(null);
  alert('Event updated successfully!');
  setCurrentScreen('eventDetail');
};

useEffect(() => {
  const onDocClick = () => { if (contextMenu.visible) hideContextMenu(); };
  window.addEventListener('click', onDocClick);
  return () => window.removeEventListener('click', onDocClick);
}, [contextMenu.visible]);


const fetchNearbyPlaces = async (lat, lng, type, limit = 50, radius = 3000) => {
  if (!GOOGLE_MAPS_API_KEY) {
    console.warn("Google Maps API Key missing – cannot fetch nearby places");
    
    return [];
  }

  // Wait for Google Maps to load
  if (!window.google || !window.google.maps) {
    console.warn("Google Maps not loaded yet");
    return [];
  }

  const typeMap = {
    'healthcare.hospital': 'hospital',
    'service.police': 'police',
    'service.fire_station': 'fire_station',
    'healthcare.pharmacy': 'pharmacy'
  };

  const googleType = typeMap[type] || null;
  if (!googleType) {
    console.warn("Invalid resource type:", type);
    return [];
  }

  const searchWithRadius = (searchRadius) => {
    return new Promise((resolve) => {
      const mapDiv = document.createElement("div");
      const map = new window.google.maps.Map(mapDiv);
      const service = new window.google.maps.places.PlacesService(map);

      service.nearbySearch(
        {
          location: { lat, lng },
          radius: searchRadius,
          type: googleType,
        },
        (results, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            results
          ) {
            const places = results.slice(0, limit).map((place) => {
              const placeTypes = place.types || [];

              // --- CATEGORY LOGIC REWRITE START ---
              let category = googleType;

              // restore doctor/clinic/hospital grouping ONLY for healthcare searches
              if (googleType === "hospital") {
                if (placeTypes.some(t => /pharmacy/i.test(t))) {
                  category = "pharmacy";
                }

                // Name‑based grouping allowed ONLY for hospital/clinic searches
                const name = (place.name || "").toLowerCase();
                if (
                  name.includes("hospital") ||
                  name.includes("clinic") ||
                  name.includes("doctor") ||
                  name.startsWith("dr ") ||
                  name.includes(" dr ")
                ) {
                  category = "hospital_clinic";
                }
              }
              // --- CATEGORY LOGIC REWRITE END ---

              return {
                id: `google-${place.place_id}`,
                name: place.name || googleType.replace("_", " "),
                type: googleType,
                category: category,
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
                address: place.vicinity || "Address not available",
                distance: calculateDistance(
                  lat,
                  lng,
                  place.geometry.location.lat(),
                  place.geometry.location.lng()
                ),
                phone: place.international_phone_number || null,
                rating: place.rating || null,
                photo: place.photos?.[0]
                  ? place.photos[0].getUrl({ maxWidth: 400 })
                  : null,
                status:
                  place.business_status === "OPERATIONAL"
                    ? "Open"
                    : "Closed",
                    types: place.types || [],
              };
            });

            resolve(
              places.sort((a, b) => a.distance - b.distance)
            );
          } else {
            resolve([]);
          }
        }
      );
    });
  };

  // 1) Try small radius (3 km)
  let results = await searchWithRadius(radius);

  // 2) If empty, expand search radius step-by-step
  if (results.length === 0) {
    const fallbackRadii = [5000, 7000, 10000, 15000]; // 5 km → 7 km → 10 km → 15 km

    for (const r of fallbackRadii) {
      console.warn(`No results in ${radius}m. Trying radius ${r}m...`);
      results = await searchWithRadius(r);
      if (results.length > 0) break;
    }
  }

  console.log(`Total ${googleType} results found:`, results.length);
  // ------- ULTRA-STRICT FIRE & POLICE FILTER -------

results = results.filter(place => {
  const name = (place.name || "").toLowerCase();
  const types = place.types || [];
  const category = (place.category || "").toLowerCase();

  // Detect medical by ANY signal
  const isMedical =
    /hospital|clinic|medical|pharma|drug|chemist|medic|dispensary/i.test(name) ||
    types.some(t => /hospital|clinic|pharmacy|health|doctor/i.test(t));

  // Detect pharmacy-specific even without name keywords
  const pharmacyLike =
    category.includes("pharmacy") ||
    types.some(t => /pharmacy|drugstore|chemist/i.test(t));

  // -------- FIRE FILTER --------
  if (googleType === "fire_station") {
    // Fire MUST have strong fire evidence
    const isFire =
      types.includes("fire_station") ||
      /(^|\s)fire(\s|$|station|brigade)/i.test(name) ||
      category.includes("fire_station");

    return isFire && !isMedical && !pharmacyLike;
  }

  // -------- POLICE FILTER --------
  if (googleType === "police") {
    const isPolice =
      types.includes("police") ||
      /police|thana|ps |station/i.test(name) ||
      category.includes("police");

    return isPolice && !isMedical && !pharmacyLike;
  }

  // -------- HOSPITAL FILTER --------
  if (googleType === "hospital") {
    return !pharmacyLike; // keep clinics + doctors
  }

  // -------- PHARMACY FILTER --------
  if (googleType === "pharmacy") {
    return pharmacyLike;
  }

  return true;
});

// Remove duplicates
results = Array.from(
  new Map(results.map(r => [r.name.toLowerCase().trim(), r])).values()
);

// Sort by distance
results.sort((a, b) => a.distance - b.distance);

// ------- END FIX -------
  return results;
};
  
useEffect(() => {
  // Run only on native app (Android/iOS), NOT on laptop web version
  if (Capacitor.getPlatform() !== 'web') {
    requestMobilePermissions();
  }
}, []);

/* Load resource requests globally */
useEffect(() => {
  if (currentScreen === "requestList" || currentScreen === "requestForm") {
    const fetchReqs = async () => {
      const colRef = collection(db, "resourceRequests");
      const q = await getDocs(colRef);

      const list = q.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(req => req.userId === auth.currentUser?.uid);

      setRequests(list.reverse());
    };

    fetchReqs();
  }
}, [currentScreen]);

// Fetch route when navigation screen is active
useEffect(() => {
  if (currentScreen === 'navigation' && selectedResource) {
    const getRoute = async () => {
      const { path, steps, distanceKm } = await fetchRoute(
        userLocation.lat,
        userLocation.lng,
        selectedResource.lat,
        selectedResource.lng
      );

      setRouteCoordinates(path);

      // Enrich steps with landmarks
      const enrichedSteps = await Promise.all(
        steps.map(step => enrichStepWithLandmarks(step))
      );
      
      setNavigationSteps(enrichedSteps);
      setCurrentStepIndex(0);

      if (distanceKm) {
        setSelectedResource(prev =>
          prev ? { ...prev, distance: distanceKm } : prev
        );
      }
    };

    getRoute();
  }
}, [currentScreen, selectedResource, userLocation]);
	
  useEffect(() => {
    if (currentScreen !== 'createEvent') return;
 
    const currentLocStr = newEventForm.location?.trim();
    const looksLikeCoords = currentLocStr && currentLocStr.includes(',');

    if (!currentLocStr || looksLikeCoords) {
      const getAddress = async () => {
        const address = await fetchExactAddress(userLocation.lat, userLocation.lng);
        if (address) {
          setNewEventForm(prev => ({ ...prev, location: address }));
        } else { 
          setNewEventForm(prev => ({
            ...prev,
            location: `Near ${userLocation.lat.toFixed(5)}, ${userLocation.lng.toFixed(5)}`
          }));
        }
      };
      getAddress();
    }
  }, [currentScreen, userLocation.lat, userLocation.lng]);


// Live clock update
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Emergency timer states
  const [emergencyTimers, setEmergencyTimers] = useState({});
  const [emergencyUpdates, setEmergencyUpdates] = useState([]);

  useEffect(() => {
    if (currentScreen === 'eventDetail' && selectedEvent) {
      // Initialize mediaFiles for createdEvents that don't have any
      if (!selectedEvent.mediaFiles) {
        selectedEvent.mediaFiles = [];
      }
      setMediaFiles(selectedEvent.mediaFiles || []);
    }
  }, [currentScreen, selectedEvent]);

  // --- Firebase init & centralized upload helper ---
  // Replace these firebaseConfig values with your project's config in production
  const firebaseConfig = {
    apiKey: "AIzaSyAWQIO_2SGUprCVggbunCLMXI2mgh5NDaE",
    authDomain: "hfd1-946b8.firebaseapp.com",
    projectId: "hfd1-946b8",
    storageBucket: "hfd1-946b8.firebasestorage.app",
    messagingSenderId: "4293306151",
    appId: "1:4293306151:web:ef2705baeb1dab54856b02",
    measurementId: "G-WTH3PB3JEX"
  };

  const firebaseApp = initializeApp(firebaseConfig);
  const auth = getAuth(firebaseApp);
  // ensure anonymous auth so app can read/write according to rules
  signInAnonymously(auth).catch(err => console.error('Anonymous auth failed:', err));

  const storage = getStorage(firebaseApp);
  const db = getFirestore(firebaseApp);
  

  /**
   * Centralized upload helper used by multiple upload inputs.
   * Returns { url, path, name } on success, or null on failure.
   */
  const uploadFile = async (file) => {
    if (!file) return null;

    try {
      // In production this uploads to Firebase Storage. Here we call uploadBytes.
      const uniqueName = `${Date.now()}_${file.name}`;
      const storagePath = `uploads/${uniqueName}`;
      const fileRef = ref(storage, storagePath);

      // uploadBytes works in browser environment where File is available
      const snapshot = await uploadBytes(fileRef, file);
      const url = await getDownloadURL(snapshot.ref);

      return { url, path: storagePath, name: uniqueName };
    } catch (err) {
      console.error('uploadFile error:', err);
      return null;
    }
  };
// Starts a MediaRecorder for microphone
const startAudioRecording = async (streamFromButton) => {
  try {
    const stream =
      streamFromButton || (await navigator.mediaDevices.getUserMedia({ audio: true }));

    recordedChunksRef.current = [];
    const options = { mimeType: "audio/webm" };
    const mr = new MediaRecorder(stream, options);

    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
    };

    mr.onstop = async () => {
      const blob = new Blob(recordedChunksRef.current, { type: "audio/webm" });
      await uploadAndSendMedia(blob, "audio");
      stream.getTracks().forEach((t) => t.stop());
    };

    mediaRecorderRef.current = mr;
    mr.start();
    setRecording(true);
  } catch (err) {
    console.error("Recording failed:", err);
    alert("Unable to access microphone.");
    setRecording(false);
  }
};

const stopAudioRecording = () => {
  try {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  } catch (err) {
    console.warn('stopAudioRecording err', err);
  } finally {
    setRecording(false);
  }
};
  // --- end firebase helper ---
const eventTypeColors = {
  "Fire": "#DC2626",             // red
  "Accident": "#EA580C",         // orange
  "Medical Emergency": "#2563EB",// blue
  "Natural Disaster": "#9333EA", // purple
  "Other": "#6B7280"             // gray
};

const getEventColor = (type) => {
  if (!type) return "#6B7280";
  const t = type.toLowerCase();

  if (t.includes("fire")) return "#DC2626";
  if (t.includes("mva") || t.includes("accident") || t.includes("collision")) return "#EA580C";
  if (t.includes("cardiac") || t.includes("medical") || t.includes("cva") || t.includes("stroke") || t.includes("respiratory"))
    return "#2563EB";
  if (t.includes("natural") || t.includes("disaster") || t.includes("earthquake") || t.includes("flood"))
    return "#9333EA";

  return "#6B7280";
};

/** Reverse geocode to get readable location */
async function getLocationName(lat, lng) {
  try {
    if (!GOOGLE_MAPS_API_KEY) {
      return { formatted: "Unknown Location", city: "Unknown-City" };
    }

    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`;
    const res = await fetch(url);
    const data = await res.json();

    if (data.status === 'OK' && data.results && data.results[0]) {
      const result = data.results[0];
      const formatted = result.formatted_address || "Unknown Location";
      
      // Extract city from address components
      let city = "Unknown-City";
      for (const component of result.address_components) {
        if (component.types.includes('locality')) {
          city = component.long_name;
          break;
        } else if (component.types.includes('administrative_area_level_2')) {
          city = component.long_name;
        }
      }

      return { formatted, city };
    }
    return { formatted: "Unknown Location", city: "Unknown-City" };
  } catch (err) {
    console.error("Geocoding failed:", err);
    return { formatted: "Unknown Location", city: "Unknown-City" };
  }
}

/** Save event to Firestore — creates eventDetails and an empty chat doc */
const saveEventToFirestore = async (eventData) => {
  try {
    // get formatted location + city
    const { formatted, city } = await getLocationName(eventData.lat, eventData.lng);

    // timestamp-based ID: HHMMSS-type-DD-MM-YYYY
    const ts = new Date();
    const hh = String(ts.getHours()).padStart(2, '0');
    const mm = String(ts.getMinutes()).padStart(2, '0');
    const ss = String(ts.getSeconds()).padStart(2, '0');
    const dateString = ts.toLocaleDateString('en-GB').replaceAll('/', '-');

    const cleanCity = (city || 'unknown-city').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    const cleanType = (eventData.type || 'event').replace(/ /g, '-').toLowerCase();

    const eventId = `${hh}${mm}${ss}-${cleanType}-${dateString}`;

    // write event details
    await setDoc(
      doc(db, "events", cleanCity, eventId, "eventDetails"),
      {
        ...eventData,
        id: eventId,
        cityName: cleanCity,
        locationName: formatted,
        createdAt: serverTimestamp()
      }
    );

    // ensure chat doc exists for this event (one doc containing messages array)
    await setDoc(
      doc(db, "events", cleanCity, eventId, "chat"),
      { messages: [] }
    );

    return { eventId, cityName: cleanCity };
  } catch (err) {
    console.error("🔥 Firestore save failed:", err);
    return null;
  }
};

/** Listen for createdEvents in Firestore */
useEffect(() => {
  const unsubscribe = onSnapshot(collection(db, "createdEvents"), (snapshot) => {
    const createdEventsFromDB = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    setCreatedEvents(createdEventsFromDB);
    //  Auto-fetch exact address for each created event
createdEventsFromDB.forEach(async (ev) => {
  if (!ev.exactAddress && typeof ev.lat === "number" && typeof ev.lng === "number") {
    const addr = await fetchExactAddress(ev.lat, ev.lng);
    if (addr) {
      try {
        await updateDoc(doc(db, "createdEvents", ev.id), { exactAddress: addr });
      } catch (e) {
        console.warn("Failed to update exactAddress:", e);
      }
    }

    // Update local state
    setCreatedEvents(prev =>
      prev.map(e =>
        e.id === ev.id ? { ...e, exactAddress: addr } : e
      )
    );
  }
});
  });

  return () => unsubscribe();
}, []);


  // Mesh Network Manager
  const MeshNetworkManager = useCallback(() => {
    let ws = null;
    let reconnectTimer = null;
    let heartbeatTimer = null;

    const connect = () => {
      try {
        ws = new WebSocket('ws://localhost:9001');
        
        ws.onopen = () => {
          console.log('Mesh network connected');
          setMeshStatus('connected');
          
          const handshake = {
            type: 'handshake',
            peerId: generatePeerId(),
            location: userLocation,
            timestamp: Date.now()
          };
          ws.send(JSON.stringify(handshake));
          
          heartbeatTimer = setInterval(() => {
            if (ws && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'heartbeat' }));
            }
          }, 30000);
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            handleMeshMessage(message);
          } catch (e) {
            console.error('Failed to parse mesh message:', e);
          }
        };

        ws.onerror = (error) => {
          console.error('Mesh network error:', error);
          setMeshStatus('disconnected');
        };

        ws.onclose = () => {
          console.log('Mesh network disconnected');
          setMeshStatus('disconnected');
          clearInterval(heartbeatTimer);
          
          reconnectTimer = setTimeout(() => {
            if (!isOnline) {
              setMeshStatus('connecting');
              connect();
            }
          }, 5000);
        };
      } catch (error) {
        console.error('Failed to connect to mesh network:', error);
        setMeshStatus('disconnected');
        simulateMeshNetwork();
      }
    };

    const disconnect = () => {
      if (ws) {
        clearInterval(heartbeatTimer);
        clearTimeout(reconnectTimer);
        ws.close();
      }
    };

    return { connect, disconnect };
  }, [userLocation, isOnline]);

  const generatePeerId = () => {
    return 'peer_' + Math.random().toString(36).substr(2, 9);
  };

  const handleMeshMessage = (message) => {
    switch (message.type) {
      case 'peer_list':
        setMeshPeers(message.peers || []);
        break;
      case 'emergency_broadcast':
        setMeshMessages(prev => [...prev, message]);
        LocalNotifications.schedule({
          notifications: [
            {
              title: "Emergency Alert via Mesh",
              body: message.content,
              id: Date.now(),
              schedule: { at: new Date(Date.now() + 1000) },
              sound: null,
              attachments: null,
              actionTypeId: "",
              extra: null
            }
          ]
        });
        break;
      case 'resource_share':
        console.log('Resource shared:', message.resource);
        break;
      case 'location_update':
        setMeshPeers(prev => 
          prev.map(p => p.id === message.peerId ? { ...p, location: message.location } : p)
        );
        break;
      default:
        console.log('Unknown mesh message type:', message.type);
    }
  };

  const simulateMeshNetwork = () => {
    setMeshStatus('connected');
    const mockPeers = [
      { id: 'peer_1', name: 'Emergency Responder 1', distance: 0.5, type: 'responder' },
      { id: 'peer_2', name: 'Medical Team', distance: 1.2, type: 'medical' },
      { id: 'peer_3', name: 'Volunteer', distance: 0.8, type: 'volunteer' }
    ];
    setMeshPeers(mockPeers);
  };

  const broadcastEmergencyMesh = (message) => {
    if (meshNode && meshNode.connect) {
      const broadcast = {
        type: 'emergency_broadcast',
        content: message,
        location: userLocation,
        timestamp: Date.now(),
        sender: generatePeerId()
      };
      
      try {
        console.log('Broadcasting via mesh:', broadcast);
        setMeshMessages(prev => [...prev, broadcast]);
        return true;
      } catch (error) {
        console.error('Failed to broadcast on mesh:', error);
        return false;
      }
    }
    return false;
  };

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setMeshStatus('disconnected');
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      setMeshStatus('connecting');
      const network = MeshNetworkManager();
      network.connect();
      setMeshNode(network);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (meshNode) {
        meshNode.disconnect();
      }
    };
  }, [MeshNetworkManager]);

// Desktop: auto-fetch location
// Mobile: wait for user to tap "Grant Permission"
useEffect(() => {
  if (!navigator.geolocation) return;

  if (!isMobile) {
    getCurrentPositionSafe()
      .then(pos => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setLocationPermission("granted");
      })
      .catch(err => {
        console.log("Desktop location denied:", err);
        setLocationPermission("denied");
      });
  }
}, []);

const requestLocation = async () => {
  try {
    const pos = await getCurrentPositionSafe();

    setUserLocation({
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
    });

    setLocationPermission("granted");
    setShowLocationDialog(false);
  } catch (err) {
    console.log("Location denied/error:", err);
    setLocationPermission("denied");
    setShowLocationDialog(false);
    alert("Unable to get location. Enable location in app settings.");
  }
};

const requestMobileLocation = async () => {
  try {
    setShowLocationDialog(false);

    const success = (pos) => {
      setUserLocation({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      });
      setLocationPermission("granted");
    };

    const error = (err) => {
      console.log("Mobile geolocation error:", err);
      alert("Unable to get location. Please enable location permissions.");
      setLocationPermission("denied");
    };

    // Unified safe location function
    try {
      const pos = await getCurrentPositionSafe();
      success({ coords: pos.coords });
    } catch (err) {
      error(err);
    }
  } catch (e) {
    console.log("Mobile geolocation exception:", e);
    alert("Error fetching your location.");
    setLocationPermission("denied");
  }
};

// Track user position for turn-by-turn navigation
useEffect(() => {
  if (currentScreen !== 'navigation' || !showNavigationPanel) return;

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      const { latitude, longitude, heading } = position.coords;
      
      setUserLocation({ lat: latitude, lng: longitude });
      if (heading !== null) setUserHeading(heading);

      // Check if user reached next step
      if (navigationSteps.length > 0 && currentStepIndex < navigationSteps.length) {
        const nextStep = navigationSteps[currentStepIndex];
        const distanceToStep = calculateDistance(
          latitude, longitude,
          nextStep.endLocation.lat, nextStep.endLocation.lng
        );

        // Move to next step if within 20 meters
        if (distanceToStep < 0.02) {
          setCurrentStepIndex(prev => Math.min(prev + 1, navigationSteps.length - 1));
        }
      }
    },
    (error) => console.error("Location tracking error:", error),
    { enableHighAccuracy: true, maximumAge: 1000, timeout: 5000 }
  );

  return () => navigator.geolocation.clearWatch(watchId);
}, [currentScreen, showNavigationPanel, navigationSteps, currentStepIndex]);
	
  useEffect(() => {
  const fetchWeather = async () => {
    try {
      // Use Open-Meteo API (no key required!)
      console.log('Fetching weather from Open-Meteo (no API key needed)...');
      const weatherRes = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${userLocation.lat}&longitude=${userLocation.lng}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&timezone=auto`
      );
      const weatherData = await weatherRes.json();
      
      if (weatherData.current) {
        // Map weather codes to conditions
        const weatherConditions = {
          0: 'Clear Sky',
          1: 'Mainly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
          45: 'Foggy', 48: 'Foggy',
          51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
          61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain',
          71: 'Light Snow', 73: 'Snow', 75: 'Heavy Snow',
          95: 'Thunderstorm'
        };
        
        const condition = weatherConditions[weatherData.current.weather_code] || 'Partly Cloudy';
        
        console.log('✓ Weather data loaded from Open-Meteo');
        setWeather({
          temp: Math.round(weatherData.current.temperature_2m),
          condition: condition,
          humidity: weatherData.current.relative_humidity_2m,
          windSpeed: Math.round(weatherData.current.wind_speed_10m),
          feelsLike: Math.round(weatherData.current.temperature_2m)
        });
        
        // Check for severe weather
        if ([95, 96, 99].includes(weatherData.current.weather_code)) {
          setWeatherAlerts([{
            type: 'Severe Weather',
            severity: 'High',
            category: 'Thunderstorm',
            headline: 'Thunderstorm Alert',
            areas: 'Your area',
            description: 'Severe weather detected in your area. Stay indoors if possible.'
          }]);
        } else {
          setWeatherAlerts([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch weather data:', error);
      setWeather({
        temp: 28,
        condition: 'Partly Cloudy',
        humidity: 65,
        windSpeed: 12,
        feelsLike: 30
      });
      setWeatherAlerts([]);
    }
  };
  
  if (isOnline) {
    fetchWeather();
    const interval = setInterval(fetchWeather, 300000);
    return () => clearInterval(interval);
  } else {
    setWeather({
      temp: 28,
      condition: 'Offline Mode',
      humidity: 65,
      windSpeed: 12,
      feelsLike: 30
    });
  }
  }, [userLocation, isOnline]);
  
// Initialize nearby resources when location is available
useEffect(() => {
  const loadNearbyResources = async () => {
    if (userLocation.lat && userLocation.lng) {
      console.log('Loading nearby emergency resources from OpenStreetMap...');
      
      try {
        // Fetch real data from OpenStreetMap Overpass API
        const [hospitals, policeStations, fireStations, pharmacies] = await Promise.all([
          fetchNearbyPlaces(userLocation.lat, userLocation.lng, 'healthcare.hospital'),
          fetchNearbyPlaces(userLocation.lat, userLocation.lng, 'service.police'),
          fetchNearbyPlaces(userLocation.lat, userLocation.lng, 'service.fire_station'),
          fetchNearbyPlaces(userLocation.lat, userLocation.lng, 'healthcare.pharmacy')
        ]);
        
        const allResources = [...hospitals, ...policeStations, ...fireStations, ...pharmacies];
        allResources.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
        
        console.log('✓ All nearby resources loaded from OSM:', allResources.length);
        
        if (allResources.length > 0) {
          setNearbyResources(allResources);
        } else {
          console.warn('No resources found nearby. This might be a rural area or API issue.');
          setNearbyResources([]);
        }
      } catch (error) {
        console.error('Failed to load nearby resources:', error);
        setNearbyResources([]);
      }
    }
  };
  
  loadNearbyResources();
}, [userLocation]);

// Check for nearby createdEvents and send notifications
/*******************************
 * Ensure eventData.id is set after saving event
 *******************************/
// (Search for usage of saveEventToFirestore and ensure the returned ID is set)
// Example event creation logic (ensure this logic is present wherever a new event is created):
// const id = await saveEventToFirestore(eventData);
// eventData.id = id;
  useEffect(() => {
      const checkNearbycreatedEvents = () => {
        // Use LocalNotifications for native, fallback to Notification for web if needed
        // Here, always use LocalNotifications as per instructions
        const allcreatedEvents = [...createdEvents];
        allcreatedEvents.forEach(event => {
          const distance = parseFloat(calculateDistance(userLocation.lat, userLocation.lng, event.lat, event.lng));
          if (distance <= 1) {
            LocalNotifications.schedule({
              notifications: [
                {
                  title: "Nearby Emergency Event",
                  body: `${event.type} - ${distance} km away. ${eventVolunteers[event.id] || 0} volunteers responding.`,
                  id: Date.now(),
                  schedule: { at: new Date(Date.now() + 1000) },
                  sound: null,
                  attachments: null,
                  actionTypeId: "",
                  extra: null
                }
              ]
            });
          }
        });
      };

      if (locationPermission === 'granted') {
        checkNearbycreatedEvents();
        const interval = setInterval(checkNearbycreatedEvents, 60000); // Check every minute
        return () => clearInterval(interval);
      }
    }, [userLocation, createdEvents, createdEvents, eventVolunteers, locationPermission]);
  
    useEffect(() => {
      if (currentScreen === 'splash') {
        setTimeout(() => setCurrentScreen('home'), 2500);
      }
    }, [currentScreen]);

  const eventCodeColors = {
    action: "bg-red-100 text-red-700",
    resource: "bg-blue-100 text-blue-700",
    medical: "bg-green-100 text-green-700",
    fire: "bg-orange-100 text-orange-700",
    hospital: "bg-purple-100 text-purple-700",
    subtype: "bg-gray-200 text-gray-800"
  };

  const eventCodes = {
    action: [
      { code: 'V', subtype: 'Volunteer Needed', useCase: 'Immediate volunteer required' },
      { code: 'E', subtype: 'Equipment Needed', useCase: 'Will call you later' },
      { code: 'S', subtype: 'Specialized Help Needed', useCase: 'Expert Response like Medic etc.' },
      { code: 'R', subtype: 'Responders Needed', useCase: 'Police, Fire or Official Responders' }
    ],
    resource: [
      { code: 'M', subtype: 'Medical Needed', useCase: 'Standard Hospital or clinic' },
      { code: 'T', subtype: 'Transport Needed', useCase: 'Ambulance, Evacuation' },
      { code: 'X', subtype: 'Extended Support', useCase: 'Backup team or Additional resources' },
      { code: 'F', subtype: 'Food/Water Needed', useCase: 'Food, Water or Basic Supplies' }
    ],
    medical: [
      { code: 'M1', resource: 'Basic Medical', description: 'First Aid Box or basic medical injury' },
      { code: 'M2', resource: 'Enhanced Medical', description: 'Includes defibrillator, stretcher, oxygen' },
      { code: 'M3', resource: 'Full Medical Cluster', description: 'Complete medical setup' }
    ],
    fire: [
      { code: 'F1', resource: 'Fire Extinguisher', description: 'Basic fire equipment' },
      { code: 'F2', resource: 'Hydrant Available', description: 'Includes hydrant and hoses' },
      { code: 'F3', resource: 'Full Fire Cluster', description: 'Complete fire response setup' }
    ],
    hospital: [
      { code: 'H1', resource: 'Standard Hospital', description: 'Full operational hospital' },
      { code: 'H2', resource: 'Emergency Ready', description: 'Critical care ready' },
      { code: 'H3', resource: 'Full Critical Care', description: 'Complete emergency facilities' }
    ],
    subtype: [
      { code: 'C', subtype: 'Cardiac', useCase: 'Heart related' },
      { code: 'A', subtype: 'Assault', useCase: 'Violence or Physical Assault' },
      { code: 'B', subtype: 'Blood Loss', useCase: 'Severe Bleeding' },
      { code: 'F', subtype: 'Fracture', useCase: 'Bone Fractures' },
      { code: 'U', subtype: 'Unconscious', useCase: 'Person Unconscious' },
      { code: 'I', subtype: 'Injury', useCase: 'Severe Injuries' }
    ]
  };

  const emergencyContacts = {
  medical: {
    title: 'Medical Emergency',
    lucideIcon: <Hospital className="w-12 h-12" />,
    color: 'bg-red-600',  
    numbers: [
        { name: 'Emergency Ambulance', number: '108', description: 'Free ambulance service' },
        { name: 'National Emergency', number: '112', description: 'All emergency services' },
        { name: 'Private Ambulance', number: '102', description: 'Alternative ambulance' }
      ]
  },
  police: {
    title: 'Police/Crime',
    lucideIcon: <Siren className="w-12 h-12" />,
    color: 'bg-blue-600',
    numbers: [
        { name: 'Police Emergency', number: '100', description: 'Police control room' },
        { name: 'National Emergency', number: '112', description: 'All emergency services' },
        { name: 'Women Helpline', number: '1091', description: 'Women safety' }
      ]
  },
  fire: {
    title: 'Fire Emergency',
    lucideIcon: <Flame className="w-12 h-12" />,
    color: 'bg-orange-600',
   numbers: [
        { name: 'Fire Service', number: '101', description: 'Fire department' },
        { name: 'National Emergency', number: '112', description: 'All emergency services' }
      ]
  },
  accident: {
    title: 'Road Accident',
    lucideIcon: <Ambulance className="w-12 h-12" />,
    color: 'bg-yellow-600',
   numbers: [
        { name: 'National Emergency', number: '112', description: 'All emergency services' },
        { name: 'Ambulance', number: '108', description: 'Medical assistance' },
        { name: 'Police', number: '100', description: 'Traffic police' }
      ]
  },
  disaster: {
    title: 'Natural Disaster',
    lucideIcon: <Mountain className="w-12 h-12" />, // or <AlertTriangle />
    color: 'bg-purple-600',
   numbers: [
        { name: 'National Emergency', number: '112', description: 'All emergency services' },
        { name: 'Disaster Helpline', number: '1078', description: 'Disaster management' },
        { name: 'NDRF', number: '011-24363260', description: 'National disaster response' }
      ]
  },
  flood: {
    title: 'Flood Emergency',
    lucideIcon: <Waves className="w-12 h-12" />,
    color: 'bg-blue-700',
   numbers: [
        { name: 'National Emergency', number: '112', description: 'All emergency services' },
        { name: 'Flood Control', number: '1070', description: 'Flood control room' },
        { name: 'NDRF', number: '011-24363260', description: 'Rescue operations' }
      ]
  },
  mental: {
    title: 'Mental Health Crisis',
    lucideIcon: <Brain className="w-12 h-12" />,
    color: 'bg-teal-600',
    numbers: [
        { name: 'Mental Health Helpline', number: '08046110007', description: 'Vandrevala Foundation' },
        { name: 'iCall', number: '9152987821', description: 'TISS counseling' },
        { name: 'NIMHANS', number: '080-46110007', description: 'Mental health support' }
      ]
  }
};

  const startEmergencyTimer = (serviceName, number) => {
    const timerConfig = {
      '108': { duration: 5, service: 'Ambulance', icon: '🚑' },
      '100': { duration: 3, service: 'Police', icon: '🚓' },
      '101': { duration: 4, service: 'Fire Service', icon: '🚒' },
      '112': { duration: 4, service: 'Emergency Services', icon: '🚨' }
    };
    
    const config = timerConfig[number] || { duration: 5, service: serviceName, icon: '🚨' };
    const timerId = `timer_${Date.now()}`;
    const arrivalTime = new Date(Date.now() + config.duration * 60000);
    
    setEmergencyTimers(prev => ({
      ...prev,
      [timerId]: {
        service: config.service,
        icon: config.icon,
        startTime: new Date(),
        arrivalTime: arrivalTime,
        duration: config.duration,
        status: 'On Route',
        number: number
      }
    }));
    
    // Simulate arrival after timer expires
    setTimeout(() => {
      setEmergencyUpdates(prev => [{
        id: timerId,
        service: config.service,
        icon: config.icon,
        message: `${config.service} has arrived at your location`,
        time: new Date()
      }, ...prev]);
      
      setEmergencyTimers(prev => {
        const updated = { ...prev };
        if (updated[timerId]) {
          updated[timerId].status = 'Arrived';
        }
        return updated;
      });
    }, config.duration * 60000);
    
    return timerId;
  };

const makeEmergencyCall = (number) => {
  const serviceMap = {
    '108': 'Ambulance',
    '100': 'Police',
    '101': 'Fire Service',
    '112': 'Emergency Services'
  };

  const serviceName = serviceMap[number] || 'Emergency Service';
  startEmergencyTimer(serviceName, number);
  setShowCallEndDialog(true);
 
  if (Capacitor.isNativePlatform()) { 
    window.location.href = `tel:${number}`;
  } else { 
    const telLink = document.createElement('a');
    telLink.href = `tel:${number}`;
    telLink.click();
 
    setTimeout(() => {
      window.location.href = `tel:${number}`;
    }, 100);
 
    setTimeout(() => {
      window.open(`tel:${number}`, '_self');
    }, 300);
  }
};

  const confirmCallEnded = () => {
    if (pendingTimerCall) {
      startEmergencyTimer(pendingTimerCall.serviceName, pendingTimerCall.number);
      setShowCallEndDialog(false);
      setPendingTimerCall(null);
    }
  };



  const activities = [
    { id: 1, type: 'CVX', desc: 'Kannankudy Block, Varkala Jamath, Masjid...', time: 'Attended at 2:22 PM', date: '12.08.2024' },
    { id: 2, type: 'MVA', desc: 'Miyakanda Road, Rasool Pallikat, 4...Media', time: 'Attended at 1:30 PM', date: '10.08.2024' }
  ];

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return (R * c);
  };

  if (currentScreen === 'splash') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-gray-700 to-gray-800 rounded-3xl flex items-center justify-center">
            <div className="text-center">
              <Heart className="w-12 h-12 text-red-500 mx-auto mb-2 animate-pulse" strokeWidth={2.5} />
              <div className="text-cyan-400 text-xs font-bold">REACH</div>
            </div>
          </div>
          <h1 className="text-white text-3xl font-bold mb-2">R.E.A.C.H</h1>
          <p className="text-gray-400 text-sm italic px-8">
            Rapid Emergency<br />Access, Care, and Help<br />Anytime, Anywhere.
          </p>
        </div>
      </div>
    );
  }

if (currentScreen === 'home') {
  return (
    <div className="min-h-screen bg-gray-100 pb-32" style={{ backgroundColor: '#F8FAB4' }}>
      
      <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between" style={{ backgroundColor: '#F08787' }}>    
	<div className="flex items-center gap-4">
          <span className="text-lg font-semibold text-gray-800">
            {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
          </span>
          <span className="text-sm font-semibold text-gray-900">
            {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {locationPermission === 'granted' ? (
            <MapPin className="w-5 h-5 text-green-600" />
          ) : (
            <MapPin className="w-5 h-5 text-gray-400" />
          )}
          {isOnline ? (
            <Wifi className="w-5 h-5 text-green-600" />
          ) : meshStatus === 'connected' ? (
            <Radio className="w-5 h-5 text-blue-600 animate-pulse" />
          ) : (
            <WifiOff className="w-5 h-5 text-red-600" />
          )}
        </div>
      </div>
 
      {weather && (
        <div className="mx-4 mt-4 bg-white rounded-2xl shadow-sm px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Thermometer className="w-8 h-8 text-blue-400" />
            <div>
              <p className="text-2xl font-semibold text-gray-800">{weather.temp}°C</p>
              <p className="text-sm text-gray-500">{weather.condition}</p>
            </div>
          </div>
          <div className="text-right space-y-1">
            <div className="flex items-center justify-end gap-2">
              <Wind className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">{weather.windSpeed} km/h</span>
            </div>
            <div className="flex items-center justify-end gap-2">
              <CloudRain className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">{weather.humidity}%</span>
            </div>
          </div>
        </div>
      )}
 
      <div className="mx-4 mt-8 space-y-5">
    
      <button
        onClick={() => setCurrentScreen('createEvent')}
        className="w-full rounded-3xl shadow-lg p-6 flex items-center justify-between hover:shadow-xl transition-all"
        style={{ backgroundColor: '#FFC7A7' }}
      >
          <div className="flex items-center gap-5">
            <div className="bg-red-50 p-4 rounded-2xl">
              <PlusCircle className="w-8 h-8 text-red-600" />
            </div>
            <div className="text-left">
              <p className="text-xl font-bold text-gray-800">Report Emergency</p>
              <p className="text-sm text-gray-600 mt-1">Create new incident alert</p>
            </div>
          </div>
          <ChevronRight className="w-6 h-6 text-gray-400" />
        </button>
 
        <button
          onClick={() => setCurrentScreen('requestForm')}
          className="w-full bg-white rounded-3xl shadow-lg p-6 flex items-center justify-between hover:shadow-xl transition-all"
        style={{ backgroundColor: '#FFC7A7' }}

		>
          <div className="flex items-center gap-5">
            <div className="bg-blue-50 p-4 rounded-2xl">
              <Edit className="w-8 h-8 text-blue-600" />
            </div>
            <div className="text-left">
              <p className="text-xl font-bold text-gray-800">Request Help</p>
              <p className="text-sm text-gray-600 mt-1">Ask for supplies or volunteers</p>
            </div>
          </div>
          <ChevronRight className="w-6 h-6 text-gray-400" />
        </button>
 
        <button
          onClick={() => setCurrentScreen('requestList')}
          className="w-full bg-white rounded-3xl shadow-lg p-6 flex items-center justify-between hover:shadow-xl transition-all"
        style={{ backgroundColor: '#FFC7A7' }}

		>
          <div className="flex items-center gap-5">
            <div className="bg-purple-50 p-4 rounded-2xl">
              <Users className="w-8 h-8 text-purple-600" />
            </div>
            <div className="text-left">
              <p className="text-xl font-bold text-gray-800">View Requests</p>
              <p className="text-sm text-gray-600 mt-1">See all active resource needs</p>
            </div>
          </div>
          <ChevronRight className="w-6 h-6 text-gray-400" />
        </button>
      </div>
 
      {showEmergencyCallMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-end justify-center pb-20">
          <div className="bg-gray-100 rounded-t-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gray-900 text-white px-6 py-5 flex items-center justify-between rounded-t-3xl">
              <h3 className="text-xl font-bold">Select Emergency Type</h3>
              <button 
                onClick={() => setShowEmergencyCallMenu(false)} 
                className="text-white text-3xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              {Object.entries(emergencyContacts).map(([key, emergency]) => (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedEmergencyType(key);
                    setShowEmergencyCallMenu(false);
                    setCurrentScreen('emergencyNumbers');
                  }}
                  className="bg-gray-800 hover:bg-gray-700 text-white rounded-2xl p-6 flex flex-col items-center justify-center gap-3 transition-all hover:scale-105 shadow-lg"
                >
                  <div className="text-4xl">
                    {emergency.lucideIcon}
                  </div>
                  <p className="font-bold text-base">{emergency.title}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
 
      <button
        onClick={() => setShowEmergencyCallMenu(true)}
        className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-red-600 hover:bg-red-700 text-white rounded-full w-12 h-12 shadow-2xl flex items-center justify-center transition-all hover:scale-110 z-50"
      >
        <Phone className="w-8 h-8" />
      </button>

      <BottomNav currentScreen={currentScreen} setCurrentScreen={setCurrentScreen} />
    </div>
  );
}

  if (currentScreen === 'emergencyNumbers' && selectedEmergencyType) {
    const emergency = emergencyContacts[selectedEmergencyType];
    return (
      <div className="min-h-screen bg-gray-50">
        <Header title={emergency.title} onBack={() => setCurrentScreen('home')} />
        <div className="p-4 pb-24">
          <div className={`${emergency.color} text-white rounded-2xl p-6 mb-4 text-center`}>
            <div className="text-5xl mb-3">{emergency.icon}</div>
            <h2 className="text-2xl font-bold">{emergency.title}</h2>
            <p className="text-sm opacity-90 mt-2">Tap any number to call immediately</p>
          </div>

          <div className="space-y-3">
            {emergency.numbers.map((contact, idx) => (
              <button
                key={idx}
                onClick={() => makeEmergencyCall(contact.number)}
                className="w-full bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left flex-1">
                    <h3 className="font-bold text-lg">{contact.name}</h3>
                    <p className="text-sm text-gray-600">{contact.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-blue-600">{contact.number}</span>
                    <Phone className="w-5 h-5 text-green-600" />
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
            <h3 className="font-bold text-yellow-900 mb-2">⚠️ Important</h3>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Stay calm and speak clearly</li>
              <li>• Provide your exact location</li>
              <li>• Describe the emergency situation</li>
              <li>• Follow dispatcher instructions</li>
            </ul>
          </div>
        </div>
        <BottomNav currentScreen="home" setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }

  if (currentScreen === 'weatherAlert') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header title="Weather Alert" onBack={() => setCurrentScreen('home')} />
        <div className="relative pb-24">
          <div className="h-96 relative">
            <MapContainer 
              center={[userLocation.lat, userLocation.lng]} 
              zoom={8} 
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap'
              />
{/* User location with navigation icon */}
              <Marker 
              position={[userLocation.lat, userLocation.lng]} 
              icon={L.divIcon({
                className: 'custom-location-marker',
                  html: `<div style="position: relative; width: 20px; height: 40px;">
                    <div style="background: #3B82F6; width: 40px; height: 40px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 4px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style="transform: rotate(45deg); margin-top: -2px; margin-left: 0px;">
                        <circle cx="12" cy="12" r="5" fill="white"/>
                    </svg>
                  </div>
                </div>`,
                iconSize: [40, 48],
                iconAnchor: [20, 44],
              })}
            >
              <Popup> Your Location</Popup>
            </Marker>
              <Circle center={[userLocation.lat, userLocation.lng]} radius={10000} color="#DC2626" fillOpacity={0.1} />
            </MapContainer>
            <div className="absolute top-4 left-4 bg-white px-3 py-2 rounded-lg shadow-lg z-[1000]">
              <p className="text-xs font-semibold text-red-600">⚠️ Active Weather Alert</p>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {weatherAlerts.length > 0 ? (
              weatherAlerts.map((alert, idx) => (
                <div key={idx} className="bg-red-600 text-white rounded-2xl p-4 min-h-[35vh] shadow-lg">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertCircle className="w-6 h-6 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-bold text-lg">{alert.headline || alert.type}</p>
                      <p className="text-sm mt-1 opacity-90">Category: {alert.category}</p>
                      <p className="text-sm opacity-90">Severity: {alert.severity}</p>
                    </div>
                  </div>
                  
                  {alert.description && (
                    <div className="bg-white bg-opacity-20 rounded-lg p-3 mb-3">
                      <p className="text-sm">{alert.description}</p>
                    </div>
                  )}
                  
                  {alert.areas && (
                    <p className="text-xs opacity-75 mb-1">📍 Affected Areas: {alert.areas}</p>
                  )}
                  
                  <div className="flex justify-between text-xs opacity-75 mt-2">
                    {alert.effective && (
                      <span>From: {new Date(alert.effective).toLocaleString()}</span>
                    )}
                    {alert.expires && (
                      <span>Until: {new Date(alert.expires).toLocaleString()}</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
                <p className="text-green-800 font-medium">✓ No active weather alerts</p>
                <p className="text-sm text-green-600 mt-1">Weather conditions are currently normal</p>
              </div>
            )}

            {weather && (
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <Cloud className="w-5 h-5 text-blue-500" />
                  Current Conditions
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <Thermometer className="w-5 h-5 text-blue-600 mb-1" />
                    <p className="text-xs text-gray-600">Temperature</p>
                    <p className="text-xl font-bold text-blue-900">{weather.temp}°C</p>
                    <p className="text-xs text-gray-500">Feels like {weather.feelsLike}°C</p>
                  </div>
                  <div className="bg-cyan-50 p-3 rounded-lg">
                    <Wind className="w-5 h-5 text-cyan-600 mb-1" />
                    <p className="text-xs text-gray-600">Wind Speed</p>
                    <p className="text-xl font-bold text-cyan-900">{weather.windSpeed} km/h</p>
                  </div>
                  <div className="bg-indigo-50 p-3 rounded-lg">
                    <CloudRain className="w-5 h-5 text-indigo-600 mb-1" />
                    <p className="text-xs text-gray-600">Humidity</p>
                    <p className="text-xl font-bold text-indigo-900">{weather.humidity}%</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <Cloud className="w-5 h-5 text-purple-600 mb-1" />
                    <p className="text-xs text-gray-600">Condition</p>
                    <p className="text-base font-bold text-purple-900">{weather.condition}</p>
                  </div>
                </div>
              </div>
            )}

            {weatherAlerts.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
                <h3 className="font-bold mb-2 text-yellow-900">⚠️ Safety Tips</h3>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• Stay indoors if possible</li>
                  <li>• Keep emergency contacts ready</li>
                  <li>• Monitor weather updates regularly</li>
                  <li>• Avoid travel unless necessary</li>
                </ul>
              </div>
            )}
          </div>
        </div>
        <BottomNav currentScreen="home" setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }

 
  /*******************************
 * REQUEST FORM (NEW VERSION)
 *******************************/
if (currentScreen === "requestForm") {

  const submitRequest = async () => {
    if (!reqName || !reqContact || !reqDescription) {
      alert("Please fill all fields");
      return;
    }

    const newReq = {
      userId: auth.currentUser?.uid || "unknown",
      name: reqName,
      contact: reqContact,
      description: reqDescription,
      location: {
        lat: userLocation.lat,
        lng: userLocation.lng,
      },
      timestamp: new Date().toISOString(),
    };

    try {
      await addDoc(collection(db, "resourceRequests"), newReq);

      alert("Request submitted!");
      setCurrentScreen("requestList"); // redirect to listing page
    } catch (err) {
      console.error("Failed to save request:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Request Form" onBack={() => setCurrentScreen("home")} />

      <div className="p-4 space-y-4 pb-24">
        <div className="bg-white rounded-2xl p-4 space-y-4">
          
          {/* NAME */}
          <div>
            <label className="block text-sm font-semibold mb-2">Name</label>
            <input
              type="text"
              value={reqName}
              onChange={(e) => setReqName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              placeholder="Enter your name"
            />
          </div>

          {/* LOCATION */}
          <div>
            <label className="block text-sm font-semibold mb-2">Location</label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              value={`${userLocation.lat.toFixed(4)}, ${userLocation.lng.toFixed(4)}`}
              readOnly
            />
          </div>

          {/* CONTACT */}
          <div>
            <label className="block text-sm font-semibold mb-2">Contact</label>
            <input
              type="tel"
              value={reqContact}
              onChange={(e) => setReqContact(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              placeholder="Enter contact number"
            />
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="block text-sm font-semibold mb-2">Description</label>
            <textarea
              value={reqDescription}
              onChange={(e) => setReqDescription(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              placeholder="Describe what assistance you need"
            />
            <p className="text-xs text-gray-500 mt-2">
              *Please provide details of assistance needed
            </p>
          </div>

          {/* SUBMIT BUTTON */}
          <button
            onClick={submitRequest}
            className="w-full bg-gray-900 text-white rounded-lg py-3 font-semibold hover:bg-gray-800"
          >
            Request
          </button>

          {/* VIEW PREVIOUS REQUESTS DROPDOWN */}
          <button
            onClick={() => setShowReqDropdown(prev => !prev)}
            className="w-full bg-blue-500 text-white rounded-lg py-3 font-semibold mt-2"
          >
            {showReqDropdown ? "Hide Requested Resources" : "View Requested Resources"}
          </button>

          {showReqDropdown && (
            <div className="bg-white rounded-xl p-4 mt-3 space-y-3 shadow">
              {requests.length === 0 ? (
                <p className="text-gray-500 text-sm text-center">No requests yet.</p>
              ) : (
                requests.map((r) => (
                  <div key={r.id} className="border rounded-lg p-3">
                    <p className="font-semibold text-sm">{r.name}</p>
                    <p className="text-gray-600 text-sm mt-1">{r.description}</p>
                    <p className="text-xs text-gray-400 mt-1">📍 {r.location.lat.toFixed(4)}, {r.location.lng.toFixed(4)}</p>
                    <p className="text-xs text-gray-400">📞 {r.contact}</p>
                    <p className="text-xs text-gray-400 mt-1">⏱ {new Date(r.timestamp).toLocaleString()}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <BottomNav currentScreen="home" setCurrentScreen={setCurrentScreen} />
    </div>
  );
}

/* Load resource requests globally */

if (currentScreen === "requestList") {
  return (
      <div className="min-h-screen" style={{ backgroundColor: '#F8FAB4' }}>      <Header title="Requested Resources" onBack={() => setCurrentScreen("home")} />

      <div className="p-4 space-y-4 pb-24">
        {requests.length === 0 ? (
          <p className="text-center text-gray-500 py-10">No requests submitted yet.</p>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="bg-white rounded-lg p-4 shadow">
                <p className="text-sm font-semibold">{r.name}</p>
                <p className="text-gray-600 text-sm mt-1">{r.description}</p>
                <p className="text-xs text-gray-400 mt-2">
                  📍 {r.location.lat.toFixed(4)}, {r.location.lng.toFixed(4)}
                </p>
                <p className="text-xs text-gray-400">
                  📞 {r.contact}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  ⏱ {new Date(r.timestamp).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={() => setCurrentScreen("requestForm")}
          className="w-full bg-blue-500 text-white rounded-lg py-3 font-semibold mt-4"
        >
          Make Another Request
        </button>
      </div>

      <BottomNav currentScreen="home" setCurrentScreen={setCurrentScreen} />
    </div>
  );
}

if (currentScreen === 'navigation' && selectedResource) {
  const destination = selectedResource;
  
  const routePath = routeCoordinates.length > 0 ? routeCoordinates : [
    [userLocation.lat, userLocation.lng],
    [destination.lat, destination.lng]
  ];
  
  return (
      <div className="min-h-screen" style={{ backgroundColor: '#F8FAB4' }}>      <Header title="" onBack={() => {
        setCurrentScreen('map');
        setSelectedResource(null);
      }} />
      <div className="relative h-screen pb-24">
        <div className="h-full">
          <MapContainer 
            center={[userLocation.lat + 0.005, userLocation.lng + 0.005]} 
            zoom={14} 
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
  <Marker position={[userLocation.lat, userLocation.lng]} icon={L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-	markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-	shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
    })}
  >
              <Popup>Your Location</Popup>
            </Marker>
            <Marker position={[destination.lat, destination.lng]} icon={createCustomIcon(
              destination.type === 'healthcare' ? '#DC2626' :
              destination.type === 'service' ? '#2563EB' :
              '#F59E0B'
            )}>
              <Popup>{destination.name}</Popup>
            </Marker>
            <Polyline positions={routePath} color="#FF6B35" weight={4} />
          </MapContainer>
        </div>
        <div className="absolute top-4 left-4 right-4 bg-white rounded-2xl p-4 shadow-lg z-[1000]">
          <div className="flex items-center justify-between">
            <button className="p-2" onClick={() => {
              setCurrentScreen('map');
              setSelectedResource(null);
            }}>
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
            <div className="flex-1 text-center">
              <p className="text-2xl font-bold text-orange-500">ETA - {Math.round(parseFloat(destination.distance) * 5)} min</p>
              <p className="text-xs text-gray-500">{destination.distance} km via Main Route</p>
            </div>
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
              <Navigation className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3 bg-green-50 rounded-lg px-3 py-2 flex items-center justify-center gap-2">
            <Activity className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-700 font-medium">On Route to {destination.name}</span>
          </div>
        </div>
        {/* Navigation Panel Toggle Button */}
        <button
          onClick={() => setShowNavigationPanel(!showNavigationPanel)}
          className="fixed bottom-32 right-4 bg-blue-500 text-white p-4 rounded-full shadow-2xl z-[1000] hover:bg-blue-600 transition-all"
        >
          <Navigation className="w-6 h-6" />
        </button>

        {/* Navigation Panel */}
        {showNavigationPanel && (
          <NavigationPanel 
            steps={navigationSteps}
            currentIndex={currentStepIndex}
            onClose={() => setShowNavigationPanel(false)}
          />
        )}
      </div>
      <BottomNav currentScreen="map" setCurrentScreen={setCurrentScreen} />
    </div>
  );
}
  if (currentScreen === 'activityHistory') {
    return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8FAB4' }}>        <Header title="Activity History" onBack={() => setCurrentScreen('profile')} />
        <div className="p-4">
          <div className="mb-4">
            <div className="relative">
              <input type="text" placeholder="Search" className="w-full px-4 py-2 pl-10 bg-gray-100 rounded-lg" />
              <div className="absolute left-3 top-2.5 text-gray-400">🔍</div>
            </div>
          </div>
<div className="space-y-3 pb-24">
  {userActivities.length > 0 ? userActivities.map(activity => (
    <div key={activity.id} className="bg-red-500 text-white rounded-2xl p-4">
      <div className="flex justify-between items-start mb-2">
       	<h3 className="font-bold text-lg">{activity.type}</h3>
       	<span className="bg-blue-500 px-3 py-1 rounded-full text-xs">See more</span>
      	</div>
      	<p className="text-sm opacity-90 mb-2">{activity.desc}</p>
      	<div className="flex justify-between items-center text-xs">
        	<span>{activity.time}</span>
        	<span>{activity.date}</span>
      	</div>
    	</div>
 	 )) : (
    	<div className="bg-white rounded-2xl p-6 text-center">
      	<p className="text-gray-500">No activity history yet</p>
      	<p className="text-sm text-gray-400 mt-2">Your volunteer responses and created createdEvents 	will appear here</p>
   	 </div>
  	)}
	</div>
        </div>
        <BottomNav currentScreen="profile" setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }

  if (currentScreen === 'eventCodes') {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F8FAB4' }}>        <Header title="Event Codes" onBack={() => setCurrentScreen('profile')} />
        <div className="p-4 pb-24">
          <div className="mb-4">
            <div className="relative">
              <input type="text" placeholder="Search" className="w-full px-4 py-2 pl-10 bg-gray-100 rounded-lg" />
              <div className="absolute left-3 top-2.5 text-gray-400">🔍</div>
            </div>
          </div>
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            {['action', 'resource', 'medical', 'fire', 'hospital', 'subtype'].map(cat => (
              <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${selectedCategory === cat ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Code</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Details</th>
                </tr>
              </thead>
              <tbody>
                {eventCodes[selectedCategory].map((item, idx) => (
                  <tr key={idx} className="border-t border-gray-100">
                    <td className="px-4 py-3 text-sm font-medium">
                      <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${eventCodeColors[selectedCategory]}`}>
                        {item.code}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{item.subtype || item.resource}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.useCase || item.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <BottomNav currentScreen="profile" setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }
 /*******************************
  * EVENT DETAIL SCREEN (CLEAN)
  *******************************/
  if (currentScreen === "eventDetail" && selectedEvent) {
    const isCreator = createdEvents.some((e) => e.id === selectedEvent.id);


    //  Firestore Live Chat Listener

    // Reverse geocode helper for full address
    const fetchExactAddress = async (lat, lng) => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
        );
        const data = await res.json();
        if (data?.display_name) {
          return data.display_name;
        }
      } catch (e) {
        console.error("Reverse geocode failed:", e);
      }
      return null;
    };


    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F8FAB4' }}>        <Header
          title="Event Details"
          onBack={() => setCurrentScreen("createdEvents")}
        />

        {/* MAP */}
	<div className="h-64">
  		<MapContainer 
    			center={[selectedEvent.lat, selectedEvent.lng]} 
    			zoom={13} 
        		style={{ height: '100%', width: '100%' }}
        		>
          		<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          		<Marker
            			position={[userLocation.lat, userLocation.lng]}
            			icon={createCustomIcon("#3B82F6")}
          		>
            			<Popup>Your Location</Popup>
          		</Marker>
          		<Marker
            			position={[selectedEvent.lat, selectedEvent.lng]}
            			icon={createCustomIcon(selectedEvent.color)}
    >
            			<Popup>{selectedEvent.type}</Popup>
          		</Marker>

    {/* Show live route */}
          		<RouteDisplay 
            			start={[userLocation.lat, userLocation.lng]} 
            			end={[selectedEvent.lat, selectedEvent.lng]} 
          		/>
        		</MapContainer>
      </div>


        {/* DETAIL CONTENT */}
        <div className="p-4 space-y-4 pb-24">
          {/* EVENT CARD */}
          <div className="bg-red-600 text-white rounded-2xl p-4 min-h-[20vh]">
            <div className="flex flex-col gap-3 mb-2">
              <div className="flex justify-between items-start">
                <h2 className="font-bold text-xl">{selectedEvent.type}</h2>

                {/* CREATOR ACTIONS */}
                {isCreator && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingEvent({ ...selectedEvent });
                        setCurrentScreen("editEvent");
                      }}
                      className="bg-white bg-opacity-20 p-2 rounded-lg hover:bg-opacity-30"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setEventToDelete(selectedEvent.id);
                        setShowDeleteConfirm(true);
                      }}
                      className="bg-white bg-opacity-20 p-2 rounded-lg hover:bg-opacity-30"
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* START TIME + LOCATION (moved up) */}
              <div className="mt-1">
                <p className="text-sm mb-1">Start Time: {selectedEvent.time?.split(' - ')[0]}</p>

                <div className="flex items-center gap-2 mb-1">
                  <button
                    onClick={() => {
                      // set selected resource and open navigation to this event
                      try {
                        setSelectedResource({ lat: selectedEvent.lat, lng: selectedEvent.lng, name: selectedEvent.location });
                      } catch (e) {
                        // fallback if setSelectedResource not present
                        window.selectedResource = { lat: selectedEvent.lat, lng: selectedEvent.lng, name: selectedEvent.location };
                      }
                      setCurrentScreen('navigation');
                    }}
                    className="flex items-center gap-2 text-sm text-white"
                    title="Open map and get directions"
                  >
                    {/* REPLACE LOCATION DISPLAY BLOCK */}
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4" />
                      <span>
                        {selectedEvent.exactAddress
                          ? selectedEvent.exactAddress
                          : selectedEvent.location}
                      </span>
                    </div>
                  </button>
                </div>

                <p className="text-sm mb-3 opacity-90">Distance: {calculateDistance(userLocation.lat, userLocation.lng, selectedEvent.lat, selectedEvent.lng)} km away</p>
              </div>

              {/* EVENT DESCRIPTION */}
              {selectedEvent.description && (
                <div className="bg-white bg-opacity-20 text-white rounded-lg p-3 text-sm">
                  <p className="font-medium mb-1">Description</p>
                  <p className="text-sm text-white/95">{selectedEvent.description}</p>
                  {/* show exact coordinates too */}
                  <p className="text-xs text-white/75 mt-2">📍 {selectedEvent.lat.toFixed(5)}, {selectedEvent.lng.toFixed(5)}</p>
                </div>
              )}

 {/* TABS: Updates | Chat | Media */}
              <div className="mt-3 flex justify-center">
                <div className="inline-flex rounded-lg p-1 bg-black">
                  <button
                    onClick={() => setActiveEventTab('updates')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${activeEventTab === 'updates' ? 'bg-white text-black' : 'bg-transparent text-white'}`}
                  >
                    Updates
                  </button>

                  <button
                    onClick={() => setActiveEventTab('chat')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${activeEventTab === 'chat' ? 'bg-white text-black' : 'bg-transparent text-white'}`}
                  >
                    Chat ({(chatMessages[selectedEvent.id] || []).length})
                  </button>

                  <button
                    onClick={() => setActiveEventTab('media')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${activeEventTab === 'media' ? 'bg-white text-black' : 'bg-transparent text-white'}`}
                  >
                    Media ({(selectedEvent.mediaFiles || []).length})
                  </button>
                </div>
              </div>

              {/* VOLUNTEERS + RESPOND BUTTON (now below tabs) */}
              <div className="flex items-center justify-between mt-3">
                <div className="bg-white rounded-lg p-3 text-gray-900 flex items-center gap-2 flex-1">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="font-bold">
                    {eventVolunteers[selectedEvent.id] || 0} Volunteer{(eventVolunteers[selectedEvent.id] || 0) !== 1 ? 's' : ''} Responding
                  </span>
                </div>
                  <button
                  onClick={() => {
                    const isAlreadyResponding = userRespondingTo.includes(selectedEvent.id);
                    
                    if (!isAlreadyResponding) {
                      setUserRespondingTo(prev => [...prev, selectedEvent.id]);
                      setEventVolunteers(prev => ({
                        ...prev,
                        [selectedEvent.id]: (prev[selectedEvent.id] || 0) + 1
                      }));
                      
                      const newActivity = {
                        id: `response_${Date.now()}`,
                        type: selectedEvent.type,
                        desc: `${selectedEvent.location} - Responded as Volunteer`,
                        time: `Attended at ${new Date().toLocaleTimeString()}`,
                        date: new Date().toLocaleDateString('en-GB'),
                        role: 'Volunteer'
                      };
                      setUserActivities(prev => [newActivity, ...prev]);
                      
                      alert('You are now responding to this event!');
                    } else {
                      setUserRespondingTo(prev => prev.filter(id => id !== selectedEvent.id));
                      setEventVolunteers(prev => ({
                        ...prev,
                        [selectedEvent.id]: Math.max(0, (prev[selectedEvent.id] || 0) - 1)
                      }));
                      alert('You have withdrawn your response.');
                    }
                  }}
                  className={`ml-2 ${
                    userRespondingTo.includes(selectedEvent.id)
                      ? 'bg-gray-500'
                      : 'bg-green-500'
                  } text-white px-4 py-2 rounded-lg font-semibold`}
                >
                  {userRespondingTo.includes(selectedEvent.id) ? '✓ Responding' : 'Respond'}
                </button>
                <div className="flex gap-2 ml-2">
                  <button 
                    onClick={async () => {
                      console.log('Fetching route from', userLocation, 'to', selectedEvent);
                      
                      try {
                        const routeCoords = await fetchRoute(
                          userLocation.lat, 
                          userLocation.lng, 
                          selectedEvent.lat, 
                          selectedEvent.lng
                        );
                        
                        console.log('Route fetched:', routeCoords.length, 'coordinates');
                        
                        if (routeCoords.length > 0) {
                          setEmergencyRoutes([{ 
                            resource: selectedEvent, 
                            route: routeCoords 
                          }]);
                          setShowEmergencyRoutes(true);
                        } else {
                          alert('Could not fetch route. Using direct line.');
                          setEmergencyRoutes([{
                            resource: selectedEvent,
                            route: [[userLocation.lat, userLocation.lng], [selectedEvent.lat, selectedEvent.lng]]
                          }]);
                          setShowEmergencyRoutes(true);
                        }
                      } catch (err) {
                        console.error('Route fetch error:', err);
                        alert('Failed to fetch route');
                      }
                    }}
                    className="bg-green-500 text-white p-2 rounded-lg hover:bg-green-600"
                  >
                    <MapPin className="w-5 h-5" />
                  </button>
                  
                  <button 
                    onClick={() => {
                      setSelectedResource(selectedEvent);
                      setCurrentScreen('navigation');
                    }}
                    className="bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Navigation className="w-5 h-5" />
                  </button>
                </div>
              </div>

            {/* Keep the rest of the tabs code below unchanged */}
          <div className="mt-3 flex justify-center" style={{display: 'none'}}>

            </div>
          </div>

          {/* SUBSCREEN: Updates | Chat | Media (inline, swap by activeEventTab) */}
          <div className="mt-4">
            {activeEventTab === 'updates' && (
  <div className="bg-white rounded-2xl p-4">
    <div className="flex justify-between items-center mb-3">
      <h3 className="font-bold">Updates</h3>
      <button 
        onClick={() => setShowAddUpdateDialog(true)}
        className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm"
      >
        + Add Update
      </button>
    </div>

    <div className="space-y-2">
      {(!eventUpdates[selectedEvent.id] || eventUpdates[selectedEvent.id].length === 0) ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          No updates yet. Star important chat messages or add manual updates.
        </div>
      ) : (
        eventUpdates[selectedEvent.id].map((update, idx) => (
          <div key={idx} className="bg-red-50 rounded-lg p-3 text-sm">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-red-800 font-medium">{update.text}</p>
                <p className="text-gray-600 text-xs mt-1">
                  By {update.sender} • {update.timestamp ? new Date(update.timestamp).toLocaleString() : 'Just now'}
                </p>
              </div>
              {update.type === 'chat' && (
                <span className="text-yellow-500 text-sm ml-2">⭐</span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
    
    {/* Add Update Dialog */}
    {showAddUpdateDialog && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 max-w-md w-full">
          <h3 className="text-lg font-bold mb-4">Add Manual Update</h3>
          <textarea
            value={newUpdateText}
            onChange={(e) => setNewUpdateText(e.target.value)}
            placeholder="Describe the current situation..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4"
            rows="4"
          />
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowAddUpdateDialog(false);
                setNewUpdateText('');
              }}
              className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() => addManualUpdate(selectedEvent.id)}
              className="flex-1 bg-blue-500 text-white py-2 rounded-lg"
            >
              Post Update
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
)}

{activeEventTab === 'media' && (
  <div className="bg-white rounded-2xl p-4">
    <h3 className="font-bold mb-3">Event Media</h3>
    
    {(!selectedEvent.mediaFiles || selectedEvent.mediaFiles.length === 0) ? (
      <p className="text-center py-6 text-gray-500 text-sm">No media uploaded yet</p>
    ) : (
      <>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {selectedEvent.mediaFiles.map((media, idx) => (
            <div
              key={idx}
              onClick={() => {
                setFullscreenMediaIndex(idx);
                setShowFullscreenMedia(true);
              }}
              className="aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-75 transition-opacity"
            >
              {media.type === 'image' && (
                <img src={media.url} className="w-full h-full object-cover" alt="" />
              )}
              {media.type === 'video' && (
                <video src={media.url} className="w-full h-full object-cover" muted />
              )}
              {media.type === 'audio' && (
                <div className="w-full h-full flex items-center justify-center bg-blue-100">
                  <span className="text-2xl">🎤</span>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <p className="text-xs text-gray-500 text-center">
          Tap any media to view fullscreen
        </p>
      </>
    )}
  </div>
)}

{/* ================= CHAT TAB ================= */}
{activeEventTab === "chat" && (
  <div className="bg-black text-white rounded-2xl p-4 flex flex-col" style={{ minHeight: "60vh" }}>

    {/* Push-to-Talk Popup */}
    {recording && (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <div className="bg-white px-6 py-4 rounded-2xl shadow-lg text-center">
          <p className="font-semibold text-lg">Push-to-Talk Active</p>
          <p className="text-sm text-gray-600 mt-1">Hold to record — release to stop</p>
        </div>
      </div>
    )}

    {/* Header */}
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-bold">Event Chat</h3>
      <button
        onClick={() => setIsChatMaximized(prev => !prev)}
        className="px-2 py-1 bg-gray-800 rounded"
      >
        {isChatMaximized ? "Minimize" : "Maximize"}
      </button>
    </div>

    {/* Messages */}
    <div className="bg-gray-900 rounded-lg p-3 mb-3 overflow-y-auto flex-1 min-h-0">
      {(!eventMessages || eventMessages.length === 0) ? (
        <p className="text-sm text-gray-300 text-center py-4">No messages yet.</p>
      ) : (
        eventMessages.map((msg, i) => {
          const isMine = msg.userId === auth.currentUser?.uid || msg.sender === "You";
          const isStarred = (eventUpdates[selectedEvent.id] || []).some(u => u.messageId === (msg.id || `msg_${i}`));
          const timeStr = msg.timestamp
            ? (msg.timestamp.toDate ? msg.timestamp.toDate() : new Date(msg.timestamp))
                .toLocaleString("en-GB", {
                  hour: "2-digit",
                  minute: "2-digit",
                  day: "2-digit",
                  month: "short",
                })
            : "";

          return (
            <div key={i} className={`mb-3 flex ${isMine ? "justify-end" : "justify-start"}`}>
              <div className="flex flex-col max-w-[80%]">
                <div className={`inline-block rounded-lg p-3 ${isMine ? "bg-blue-500 text-white" : "bg-gray-700 text-white"}`}>
                  <div className="text-xs font-semibold mb-1">
                    {msg.sender} {msg.isVolunteer ? "(Volunteer)" : "(Bystander)"}
                  </div>

                  {msg.text && <div className="text-sm mb-1">{msg.text}</div>}

                  {msg.media?.type === "image" && (
                    <img
                      src={msg.media.url}
                      className="mt-2 rounded-lg"
                      style={{ maxWidth: "180px", maxHeight: "180px", objectFit: "cover" }}
                      alt=""
                    />
                  )}

                  {msg.media?.type === "video" && (
                    <video
                      src={msg.media.url}
                      controls
                      className="mt-2 rounded-lg"
                      style={{ maxWidth: "200px" }}
                    />
                  )}

                  {msg.media?.type === "audio" && (
                    <audio src={msg.media.url} controls className="mt-2 w-full" />
                  )}

                  <div className="text-xs opacity-75 mt-2 text-right">{timeStr}</div>
                </div>
                
                {/* Star button to add to updates */}
                {msg.text && (
                  <button
                    onClick={() => toggleMessageAsUpdate(selectedEvent.id, msg.id || `msg_${i}`, msg)}
                    className={`text-xs mt-1 px-2 py-1 rounded self-start ${isStarred ? 'bg-yellow-500 text-white' : 'bg-gray-600 text-gray-200'}`}
                  >
                    {isStarred ? '⭐ Starred' : '☆ Add to Updates'}
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}

      {isTyping && (
        <div className="text-left mt-1">
          <div className="inline-block bg-gray-700 text-white px-3 py-1 rounded-lg text-xs opacity-80 animate-pulse">
            Someone is typing…
          </div>
        </div>
      )}
    </div>

    {/* Input Row */}
    <div className="flex items-center gap-2 p-2 bg-white border-t">

      {/* Push-to-talk mic */}
      <button
        onMouseDown={async () => {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            startAudioRecording(stream);
          } catch (err) {
            alert("Microphone permission is required to record audio.");
          }
        }}
        onMouseUp={stopAudioRecording}
        onTouchStart={async () => {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            startAudioRecording(stream);
          } catch (err) {
            alert("Microphone permission is required to record audio.");
          }
        }}
        onTouchEnd={stopAudioRecording}
        className={`p-2 rounded-full ${
          recording ? "bg-red-500 text-white" : "bg-gray-200"
        }`}
      >
        🎤
      </button>

      {/* File upload */}
      <button
        onClick={() => chatFileInputRef.current?.click()}
        className="p-2 rounded-full bg-gray-200"
      >
        📎
      </button>

      {/* Input + Send combined box */}
      <div className="flex items-center flex-1 bg-gray-100 rounded-xl px-3 py-2 border border-gray-300">
        <input
          type="text"
          value={currentChatMessage}
          onChange={(e) => setCurrentChatMessage(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendTextMessage(selectedEvent.id)}
          placeholder="Type a message…"
          className="flex-1 bg-transparent text-black text-sm outline-none"
        />

        <button
          onClick={() => sendTextMessage(selectedEvent.id)}
          className="bg-blue-500 text-white px-3 py-1.5 rounded-lg text-sm ml-2"
        >
          Send
        </button>
      </div>
    </div>

    {/* Hidden file input */}
    <input
      ref={chatFileInputRef}
      type="file"
      accept="image/*,video/*,audio/*"
      className="hidden"
      onChange={async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;

        let type = "image";
        if (f.type.startsWith("video")) type = "video";
        if (f.type.startsWith("audio")) type = "audio";

        await uploadAndSendMedia(f, type);
        e.target.value = null;
      }}
    />

    {/* ============ Fullscreen Slideshow Modal ============ */}
    {showFullscreenMedia && selectedEvent && (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-transparent">
          {/* Header: slide index + close */}
          <div className="flex items-center justify-between mb-3 text-white">
            <div className="text-sm">{(fullscreenMediaIndex + 1)}/{(selectedEvent.mediaFiles || []).length}</div>
            <div className="flex gap-2">
              <button onClick={() => setShowFullscreenMedia(false)} className="px-3 py-1 bg-white bg-opacity-10 rounded">Close</button>
            </div>
          </div>
          {/* Custom Slideshow Block */}
          <div className="w-full flex flex-col items-center justify-center text-white">
            {/* Slide content (no borders) */}
            <div className="flex items-center justify-center mb-4" style={{ maxHeight: '70vh' }}>
              {(() => {
                const media = (selectedEvent.mediaFiles || [])[fullscreenMediaIndex];
                if (!media) return null;

                if (media.type === 'image') {
                  return (
                    <img src={media.url} className="max-h-[70vh] max-w-full object-contain" alt="slide" />
                  );
                }

                if (media.type === 'video') {
                  return (
                    <video src={media.url} controls className="max-h-[70vh] max-w-full" />
                  );
                }

                if (media.type === 'audio') {
                  return (
                    <div className="p-6 bg-gray-900 rounded-lg flex items-center justify-center">
                      <AudioBubble url={media.url} isMine={true} />
                    </div>
                  );
                }

                return null;
              })()}
            </div>
            {/* Prev / Delete / Next (below the media, centered) */}
            <div className="flex items-center justify-center gap-6 mt-4">
              <button
                onClick={() => setFullscreenMediaIndex(i => Math.max(0, i - 1))}
                className="px-4 py-2 bg-white bg-opacity-10 rounded-lg text-white"
              >
                ◀ Prev
              </button>
              <button
                onClick={async () => {
                  const media = selectedEvent.mediaFiles[fullscreenMediaIndex];
                  await deleteMediaItem(selectedEvent.id, media);
                
                  const updated = selectedEvent.mediaFiles.filter(
                    (_, i) => i !== fullscreenMediaIndex
                  );
                
                  if (updated.length === 0) {
                    setShowFullscreenMedia(false);
                  } else {
                    setFullscreenMediaIndex(i =>
                      Math.min(updated.length - 1, i)
                    );
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg"
              >
                Delete
              </button>
              <button
                onClick={() => setFullscreenMediaIndex(i =>
                  Math.min((selectedEvent.mediaFiles || []).length - 1, i + 1)
                )}
                className="px-4 py-2 bg-white bg-opacity-10 rounded-lg text-white"
              >
                Next ▶
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
)}
          </div>

          {/* CONTEXT MENU */}
          {contextMenu.visible && contextMenu.event && (
            <div
              style={{
                position: "fixed",
                left: contextMenu.x,
                top: contextMenu.y,
                zIndex: 10000,
              }}
              className="bg-white rounded-md shadow-lg"
            >
              <button
                onClick={() => startEditEvent(contextMenu.event)}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Edit
              </button>

              <button
                onClick={() => deleteCreatedEvent(contextMenu.event.id)}
                className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100"
              >
                Delete
              </button>

              <button
                onClick={updateEvent}
                className="w-full bg-blue-500 text-white rounded-lg py-3 font-semibold hover:bg-blue-600"
              >
                Update Event
              </button>
            </div>
          )}

          <BottomNav
            currentScreen="createdEvents"
            setCurrentScreen={setCurrentScreen}
          />
  	{showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 w-64 text-center">
                <h3 className="font-bold text-lg mb-4">Delete this event?</h3>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 py-2 rounded-lg bg-gray-200"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={() => deleteCreatedEvent(eventToDelete)}
                    className="flex-1 py-2 rounded-lg bg-red-600 text-white"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <BottomNav currentScreen="createdEvents" setCurrentScreen={setCurrentScreen} />
      </div>
    </div>
    );
  }

  if (currentScreen === 'createEvent') {
    return (
      <>
      <div className="min-h-screen" style={{ backgroundColor: '#F8FAB4' }}>        <Header title="Create Event" onBack={() => setCurrentScreen('home')} />
        <div className="p-4 space-y-4 pb-24">
          <div className="bg-white rounded-2xl p-4 space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Incident Type</label>
              <select
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                value={newEventForm.incidentType}
                onChange={(e) => setNewEventForm({ ...newEventForm, incidentType: e.target.value })}
              >
                <option value="">Select incident type</option>
                <option value="Medical Emergency">Medical Emergency</option>
                <option value="Fire">Fire</option>
                <option value="Accident">Accident</option>
                <option value="Natural Disaster">Natural Disaster</option>
                <option value="Other">Other</option>
              </select>
            </div>
   
            <div>
              <label className="block text-sm font-semibold mb-2">Location</label>
              <input
                type="text"
                placeholder="Loading address from GPS..."
                value={newEventForm.location || ''}
                onChange={(e) => setNewEventForm({ ...newEventForm, location: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white"
              />
              <p className="text-xs text-gray-500 mt-1">
                You can add extra details (e.g. "3rd floor", "behind temple")
              </p>
            </div>
  
            {/* Volunteers Required */}
            <div>
              <label className="block text-sm font-semibold mb-2">Number of Volunteers Required</label>
              <input
                type="number"
                min="1"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                value={newEventForm.volunteersNeeded}
                onChange={(e) =>
                  setNewEventForm({ ...newEventForm, volunteersNeeded: parseInt(e.target.value) })
                }
              />
            </div>
  
            {/* Supplies */}
            <div>
              <label className="block text-sm font-semibold mb-2">Emergency Supplies Needed</label>
              <textarea
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                rows="3"
                value={newEventForm.suppliesNeeded}
                onChange={(e) => setNewEventForm({ ...newEventForm, suppliesNeeded: e.target.value })}
              />
            </div>
  
            {/* Emergency Services */}
            <div>
              <label className="block text-sm font-semibold mb-2">Emergency Service Status</label>
              <div className="space-y-2">
                {["Not Arrived", "On Route", "Arrived"].map((status) => (
                  <label key={status} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="serviceStatus"
                      value={status}
                      checked={newEventForm.emergencyServiceStatus === status}
                      onChange={(e) => setNewEventForm({ ...newEventForm, emergencyServiceStatus: e.target.value })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{status}</span>
                  </label>
                ))}
              </div>
            </div>
  
            {/* Upload Media */}
            <div>
              <label className="block text-sm font-semibold mb-2">Upload Media</label>
  
              <div className="space-y-2">
  
                {/* IMAGES */}
                <button
                  onClick={() => document.getElementById("eventImageInput").click()}
                  className="w-full bg-gray-100 text-gray-700 rounded-lg p-4 flex items-center justify-center gap-2 hover:bg-gray-200"
                >
                  <Camera className="w-5 h-5" />
                  <span>Add Photos</span>
                </button>
                <input
                  id="eventImageInput"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setNewEventForm({
                      ...newEventForm,
                      mediaFiles: [
                        ...newEventForm.mediaFiles,
                        ...files.map((f) => ({ type: "image", file: f })),
                      ],
                    });
                  }}
                />
  
                {/* VIDEOS */}
                <button
                  onClick={() => document.getElementById("eventVideoInput").click()}
                  className="w-full bg-gray-100 text-gray-700 rounded-lg p-4 flex items-center justify-center gap-2 hover:bg-gray-200"
                >
                  <Video className="w-5 h-5" />
                  <span>Add Videos</span>
                </button>
                <input
                  id="eventVideoInput"
                  type="file"
                  accept="video/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setNewEventForm({
                      ...newEventForm,
                      mediaFiles: [
                        ...newEventForm.mediaFiles,
                        ...files.map((f) => ({ type: "video", file: f })),
                      ],
                    });
                  }}
                />
              </div>
  
              {/* PREVIEW SELECTED FILES */}
              {newEventForm.mediaFiles.length > 0 && (
                <div className="mt-3">
                  <h3 className="text-lg font-bold mb-2">Uploaded Files</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {newEventForm.mediaFiles.length} file(s)
                  </p>

                  <div className="grid grid-cols-4 gap-2">
                    {newEventForm.mediaFiles.map((media, idx) => (
                      <div
                        key={idx}
                        className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center relative overflow-hidden"
                      >
                        {media.type === "image" ? (
                          <img
                            src={URL.createObjectURL(media.file)}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <video
                            src={URL.createObjectURL(media.file)}
                            className="w-full h-full object-cover"
                            muted
                          />
                        )}

                        {/* DELETE BUTTON */}
                        <button
                          onClick={() =>
                            setNewEventForm({
                              ...newEventForm,
                              mediaFiles: newEventForm.mediaFiles.filter((_, i) => i !== idx),
                            })
                          }
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
  
          {/* SUBMIT BUTTON */}
          <button
            onClick={async () => {
              console.log("FILES TO UPLOAD:", newEventForm.mediaFiles);
              
              // Upload files first
              const uploadedMedia = [];
              for (const media of newEventForm.mediaFiles) {
                if (!media.file) continue;
                const uploaded = await uploadFile(media.file);
                if (uploaded) {
                  uploadedMedia.push({
                    type: media.type,
                    url: uploaded.url,
                    path: uploaded.path,
                    uploadedAt: Date.now(),
                  });
                }
              }

              // Build event object
              const newEventData = {
                type: newEventForm.incidentType,
                location: newEventForm.location,
                lat: userLocation.lat,
                lng: userLocation.lng,
                volunteersNeeded: newEventForm.volunteersNeeded,
                suppliesNeeded: newEventForm.suppliesNeeded,
                emergencyServiceStatus: newEventForm.emergencyServiceStatus,
                mediaFiles: uploadedMedia,
                color: "#DC2626",
                time: new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                createdAt: serverTimestamp(),
              };

              // Save to Firestore
              const docRef = await addDoc(collection(db, "createdEvents"), newEventData);
              const eventWithId = { ...newEventData, id: docRef.id };

              // ADD THIS: Track activity locally
              const newActivity = {
                id: docRef.id,
                type: newEventForm.incidentType,
                desc: `${newEventForm.location}`,
                time: `Created at ${new Date().toLocaleTimeString()}`,
                date: new Date().toLocaleDateString('en-GB'),
                role: 'Creator'
              };
              setUserActivities(prev => [newActivity, ...prev]);

              // Save locally
              setCreatedEvents((prev) => [eventWithId, ...prev]);

              // Navigate to detail
              setSelectedEvent(eventWithId);
              setCurrentScreen("eventDetail");

              // Notification
              if (Notification.permission === 'granted') {
                LocalNotifications.schedule({
                  notifications: [
                    {
                      title: "Event Created",
                      body: "Your event has been created successfully!",
                      id: Date.now(),
                      schedule: { at: new Date(Date.now() + 1000) },
                      sound: null,
                      attachments: null,
                      actionTypeId: "",
                      extra: null
                    }
                  ]
                });
              }
              
              alert('Event created successfully!');
              
              // Clear form
              setNewEventForm({
                incidentType: "",
                location: "",
                volunteersNeeded: 1,
                suppliesNeeded: "",
                emergencyServiceStatus: "Not Arrived",
                mediaFiles: []
              });
              setMediaFiles([]);
            }}
            className="w-full bg-red-600 text-white rounded-xl p-4 text-lg font-semibold"
          >
            Create Event
          </button>
        </div>
      </div>
      <BottomNav currentScreen={currentScreen} setCurrentScreen={setCurrentScreen} />
  </>
    );
  }
  
  if (currentScreen === 'editEvent' && editingEvent) {
    return (
  <div className="min-h-screen" style={{ backgroundColor: '#F8FAB4' }}>        <Header title="Edit Event" onBack={() => {
          setEditingEvent(null);
          setCurrentScreen('eventDetail');
        }} />
        <div className="p-4 space-y-4 pb-24">
          <div className="bg-white rounded-2xl p-4 space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Incident Type</label>
              <select 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                value={editingEvent.type}
                onChange={(e) => setEditingEvent({...editingEvent, type: e.target.value})}
              >
                <option value="Medical Emergency">Medical Emergency</option>
                <option value="Fire">Fire</option>
                <option value="Accident">Accident</option>
                <option value="Natural Disaster">Natural Disaster</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Location</label>
              <input 
                type="text" 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg" 
                value={editingEvent.location}
                onChange={(e) => setEditingEvent({...editingEvent, location: e.target.value})}
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Number of Volunteers Required</label>
              <input 
                type="number" 
                min="1"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg" 
                value={editingEvent.volunteersNeeded}
                onChange={(e) => setEditingEvent({...editingEvent, volunteersNeeded: parseInt(e.target.value)})}
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Emergency Supplies Needed</label>
              <textarea 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg" 
                rows="3"
                value={editingEvent.suppliesNeeded}
                onChange={(e) => setEditingEvent({...editingEvent, suppliesNeeded: e.target.value})}
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">Emergency Service Status</label>
              <div className="space-y-2">
                {['Not Arrived', 'On Route', 'Arrived'].map(status => (
                  <label key={status} className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio"
                      name="serviceStatus"
                      value={status}
                      checked={editingEvent.emergencyServiceStatus === status}
                      onChange={(e) => setEditingEvent({...editingEvent, emergencyServiceStatus: e.target.value})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{status}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <button 
              onClick={updateEvent}
              className="w-full bg-blue-500 text-white rounded-lg py-3 font-semibold hover:bg-blue-600"
            >
              Update Event
            </button>
          </div>
        </div>
        <BottomNav currentScreen={currentScreen} setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }
  
  if (currentScreen === 'createdEvents') {
    return (
<     div className="min-h-screen" style={{ backgroundColor: '#F8FAB4' }}>        <Header title="Active Events" onBack={() => setCurrentScreen('home')} />
        <div className="h-64">
          <MapContainer 
            center={[userLocation.lat, userLocation.lng]} 
            zoom={6} 
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker 
              position={[userLocation.lat, userLocation.lng]} 
              icon={L.divIcon({
                className: 'custom-location-marker',
                  html: `<div style="position: relative; width: 20px; height: 40px;">
                    <div style="background: #3B82F6; width: 40px; height: 40px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 4px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style="transform: rotate(45deg); margin-top: -2px; margin-left: 0px;">
                        <circle cx="12" cy="12" r="5" fill="white"/>
                    </svg>
                  </div>
                </div>`,
                iconSize: [40, 48],
                iconAnchor: [20, 44],
              })}
            >
              <Popup> Your Location</Popup>
            </Marker>
	{[...createdEvents]
        	      .filter(event => {
                	const distance = parseFloat(calculateDistance(userLocation.lat, 	userLocation.lng, event.lat, event.lng));
                return distance <= 1;
              })
              .map(event => (
              <Marker
                key={event.id} 
                position={[event.lat, event.lng]} 
                icon={createCustomIcon(getEventColor(event.type))}
                eventHandlers={{
                  click: () => {
                    const full = createdEvents.find(ev => ev.id === event.id) || event;
                    setSelectedEvent(full);
                    setCurrentScreen('eventDetail');
                  }
                }}
              >
                <Popup>
                  <strong>{event.type}</strong><br />
                  {event.time}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
<div className="p-4 space-y-3 pb-24">
	{/* Show emergency routes */}
{showEmergencyRoutes && emergencyRoutes.length > 0 && (
  <div className="mb-4 bg-green-50 border border-green-200 rounded-xl p-3">
    <p className="text-sm font-semibold text-green-900 mb-2"> Showing route</p>
    <button 
      onClick={() => {
        setShowEmergencyRoutes(false);
        setEmergencyRoutes([]);
      }}
      className="text-xs bg-green-600 text-white px-3 py-1 rounded"
    >
      Clear Route
    </button>
  </div>
)}
          <h3 className="font-bold text-lg">Active Events Within 1km</h3>
          {[...createdEvents]
            .filter(event => {
              const distance = parseFloat(calculateDistance(userLocation.lat, userLocation.lng, event.lat, event.lng));
              return distance <= 1;
            })
            .sort((a, b) => {
              const distA = parseFloat(calculateDistance(userLocation.lat, userLocation.lng, a.lat, a.lng));
              const distB = parseFloat(calculateDistance(userLocation.lat, userLocation.lng, b.lat, b.lng));
              return distA - distB;
            })
            .map(event => (<div
              key={event.id}
              onClick={() => { setSelectedEvent(event); setCurrentScreen('eventDetail'); }}
              onContextMenu={(e) => handleContextMenu(e, event)}
              className="rounded-2xl p-4 text-white cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: getEventColor(event.type) }}
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-lg">{event.type}</h3>
                <button className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-xs">View Details</button>
              </div>
              <p className="text-sm opacity-90 mb-1">{event.time}</p>
              <p className="text-xs flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />
                {event.exactAddress ?? event.locationName ?? event.location ?? "Fetching address..."}
              </p>
              <p className="text-xs opacity-75 mt-1">
                {calculateDistance(userLocation.lat, userLocation.lng, event.lat, event.lng)} km away
              </p>
            </div>
          ))}
        </div>
        <BottomNav currentScreen={currentScreen} setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }
  const createEmojiIcon = (emoji) => {
    return L.divIcon({
      html: `
        <div style="
          font-size: 28px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: white;
          border: 2px solid #333;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        ">${emoji}</div>
      `,
      className: "",
      iconSize: [40, 40],
      iconAnchor: [20, 40]
    });
  };
  if (currentScreen === 'map') {
      return (
        <div className="min-h-screen bg-gray-50">
          <Header title="Nearby Resources" onBack={() => setCurrentScreen('home')}/>
          <div className="h-96">
            <MapContainer 
              center={[userLocation.lat, userLocation.lng]} 
              zoom={13} 
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker 
                position={[userLocation.lat, userLocation.lng]} 
                icon={L.divIcon({
                  className: 'custom-location-marker',
                  html: `<div style="position: relative; width: 20px; height: 40px;">
                    <div style="background: #3B82F6; width: 40px; height: 40px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 4px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style="transform: rotate(45deg); margin-top: -2px; margin-left: 0px;">
                        <circle cx="12" cy="12" r="5" fill="white"/>
                      </svg>
                    </div>
                  </div>`,
                  iconSize: [40, 48],
                  iconAnchor: [20, 44],
                })}
              >
                <Popup> Your Location</Popup>
              </Marker>
              {/* =========== Emoji Marker Icon =========== */}
              {/*
                Insert createEmojiIcon function just above first usage of createCustomIcon below
              */}
              {(() => {
                // Add the createEmojiIcon function here
                
                return null;
              })()}
              <MarkerClusterGroup chunkedLoading></MarkerClusterGroup>
              {nearbyResources
                .filter(r =>
                  resourceFilter === 'all'
                    ? true
                    : resourceFilter === 'healthcare'
                    ? (r.type === 'hospital' || r.category === 'hospital_clinic')
                    : resourceFilter === 'police'
                    ? (r.type === 'police')
                    : resourceFilter === 'fire'
                    ? (r.type === 'fire_station')
                    : resourceFilter === 'pharmacy'
                    ? (r.type === 'pharmacy' || r.category === 'pharmacy')
                    : false
                )
                .map(resource => (
                  <Marker 
                    key={resource.id}
                    position={[resource.lat, resource.lng]} 
                    icon={createEmojiIcon(
                      (resource.type === 'hospital' || resource.category === 'hospital_clinic') ? "🏥" :
                      (resource.type === 'pharmacy' || resource.category === 'pharmacy') ? "💊" :
                      resource.type === 'police' ? "👮" :
                      resource.type === 'fire_station' ? "🚒" :
                      "📍"
                    )}
                  >
                    <Popup>
                      <strong>
                        {(resource.type === 'hospital' || resource.category === 'hospital_clinic') ? '🏥 ' :
                          resource.type === 'police' ? '👮 ' :
                          resource.type === 'fire_station' ? '🚒 ' :
                          (resource.type === 'pharmacy' || resource.category === 'pharmacy') ? '💊 ' :
                          '📍 '}
                        {resource.name}
                      </strong><br />
                      {resource.distance} km away<br />
                      Status: {resource.status}
                    </Popup>
                  </Marker>
                ))}

            {/* Only show route for selected resource */}
            {selectedResource && (
              <RouteDisplay 
                start={[userLocation.lat, userLocation.lng]} 
                end={[selectedResource.lat, selectedResource.lng]} 
              />
            )}
              <Circle center={[userLocation.lat, userLocation.lng]} radius={2000} color="#3B82F6" fillOpacity={0.05} />
		{/* Show emergency routes on map */}
		{showEmergencyRoutes && emergencyRoutes.map((item, idx) => (
  		<Polyline 
    		key={idx} 
    		positions={item.route} 
    		color="#10B981" 
    		weight={4} 
  		/>
		))}
            </MapContainer>
          </div>
          <div className="p-4 space-y-3 pb-24">
            {/* Resource Category Tabs */}
<div className="flex gap-2 mb-3 overflow-x-auto">
  {[
    { key: 'all', label: 'All', icon: '📍' },
    { key: 'healthcare', label: 'Hospitals', icon: '🏥' },
    { key: 'police', label: 'Police', icon: '👮' },
    { key: 'fire', label: 'Fire', icon: '🔥' },
    { key: 'pharmacy', label: 'Pharmacy', icon: '💊' }
  ].map(tab => (
    <button
      key={tab.key}
      onClick={() => setResourceFilter(tab.key)}
      className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 whitespace-nowrap ${
        resourceFilter === tab.key ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border'
      }`}
    >
      <span>{tab.icon}</span>
      <span className="ml-1">{tab.label}</span>
    </button>
  ))}
</div>
            <h3 className="font-bold text-lg">Nearby Emergency Resources</h3>
            
{nearbyResources
  .filter(r =>
    resourceFilter === 'all'
      ? true
      : resourceFilter === 'healthcare'
      ? (r.type === 'hospital' || r.category === 'hospital_clinic')
      : resourceFilter === 'police'
      ? (r.type === 'police')
      : resourceFilter === 'fire'
      ? (r.type === 'fire_station')
      : resourceFilter === 'pharmacy'
      ? (r.type === 'pharmacy' || r.category === 'pharmacy')
      : false
  )
  .sort((a, b) => (a.distance || 0) - (b.distance || 0))
  .map(resource => (
    <div key={resource.id} className="bg-white rounded-xl p-4 flex justify-between items-center shadow-sm">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-2xl leading-none">
            { (resource.type === 'hospital' || resource.category === 'hospital_clinic') ? '🏥' :
              resource.type === 'police' ? '👮' :
              resource.type === 'fire_station' ? '🚒' :
              resource.type === 'pharmacy' ? '💊' :
              '📍' }
          </span>
          <h4 className="font-semibold">{resource.name}</h4>
        </div>
        <p className="text-sm text-gray-500">
          {resource.distance != null ? (
            resource.distance < 1
              ? `${Math.round(resource.distance * 1000)} m away`
              : `${resource.distance.toFixed(2)} km away`
          ) : 'Distance unknown'}
        </p>
        {resource.status && (
          <span className={`text-xs px-2 py-1 rounded-full mt-1 inline-block ${
            resource.status === 'On-route' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
          }`}>
            {resource.status}
          </span>
        )}
      </div>

      <button
        onClick={() => {
          setSelectedResource(resource);
          setCurrentScreen('navigation');
        }}
        className="bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 transition-colors ml-3"
      >
        <Navigation className="w-5 h-5" />
      </button>
    </div>
  ))}
          </div>
          <BottomNav currentScreen="map" setCurrentScreen={setCurrentScreen} />
        </div>
      );
    }
  

  if (currentScreen === 'feedback') {
    return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8FAB4' }}>        <Header title="Feedback & Community" onBack={() => setCurrentScreen('profile')} />
        <div className="p-4 pb-24">
          <div className="bg-white rounded-2xl p-6 mb-4">
            <h2 className="text-xl font-bold mb-4">Share Your Feedback</h2>
            <p className="text-sm text-gray-600 mb-6">Help us improve R.E.A.C.H by sharing your experience</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Your Name</label>
                <input type="text" className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="Enter your name" />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Email</label>
                <input type="email" className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="Enter your email" />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Feedback Type</label>
                <select className="w-full px-4 py-3 border border-gray-300 rounded-lg">
                  <option>Bug Report</option>
                  <option>Feature Request</option>
                  <option>General Feedback</option>
                  <option>Appreciation</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Your Message</label>
                <textarea 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg" 
                  rows="5" 
                  placeholder="Tell us what you think..."
                ></textarea>
              </div>
              
              <button className="w-full bg-blue-500 text-white rounded-lg py-3 font-semibold hover:bg-blue-600">
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
        <BottomNav currentScreen="profile" setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }

  if (currentScreen === 'notifications') {
    return (
<div className="min-h-screen" style={{ backgroundColor: '#F8FAB4' }}>        <Header title="Notification Settings" onBack={() => setCurrentScreen('profile')} />
        <div className="p-4 pb-24">
          <div className="bg-white rounded-2xl p-6 mb-4">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-blue-500" />
              </div>
              <h2 className="text-xl font-bold mb-2">Enable Notifications</h2>
              <p className="text-sm text-gray-600">Stay informed about emergency alerts and updates</p>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                <div className="text-green-600 text-xl"></div>
                <div>
                  <p className="font-medium text-green-900">Emergency Alerts</p>
                  <p className="text-xs text-green-700">Get notified about nearby emergencies</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="text-blue-600 text-xl"></div>
                <div>
                  <p className="font-medium text-blue-900">Weather Warnings</p>
                  <p className="text-xs text-blue-700">Receive severe weather alerts</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="text-purple-600 text-xl"></div>
                <div>
                  <p className="font-medium text-purple-900">Location Updates</p>
                  <p className="text-xs text-purple-700">Updates when help is nearby</p>
                </div>
              </div>
            </div>
            
            <button 
              onClick={() => {
                if ('Notification' in window) {
                  Notification.requestPermission().then(permission => {
                    if (permission === 'granted') {
                      alert('Notifications enabled successfully!');
                    } else {
                      alert('Notification permission denied. Please enable in browser settings.');
                    }
                  });
                }
              }}
              className="w-full bg-blue-500 text-white rounded-lg py-3 font-semibold hover:bg-blue-600 mb-3"
            >
              Enable Notifications
            </button>
            
            <p className="text-xs text-center text-gray-500">
              Current Status: {Notification.permission === 'granted' ? '✓ Enabled' : 
                              Notification.permission === 'denied' ? '✗ Denied' : '⚠ Not Set'}
            </p>
          </div>
        </div>
        <BottomNav currentScreen="profile" setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }

  if (currentScreen === 'cpr') {
    return (
<div className="min-h-screen" style={{ backgroundColor: '#F8FAB4' }}>        <Header title="CPR for Adults" onBack={() => setCurrentScreen('profile')} />
        <div className="p-4 space-y-6 pb-24">
          <div className="bg-white rounded-2xl p-6">
            <h2 className="font-bold text-xl mb-4">CPR Instructions</h2>
            <div className="space-y-4">
              {[
                'CHECK the scene for safety and use PPE',
                'CHECK for responsiveness and breathing',
                'CALL 9-1-1 and get equipment',
                'Place person on back on firm surface',
                'Deliver chest compressions at 100-120/min',
                'Open airway, pinch nose, give rescue breaths'
              ].map((text, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">{i + 1}</div>
                  <p className="text-gray-700 pt-1">{text}</p>
                </div>
              ))}
            </div>
          </div>
<div className="bg-white rounded-2xl p-6">
            <h3 className="font-semibold mb-3">Hand Position & Technique</h3>
            
            {/* Image 1: Rescue breathing */}
            <div className="mb-4">
              <div className="bg-blue-50 rounded-xl overflow-hidden">
                <img
                  src="/Rescue_breathing.png"
                  alt="CPR Hand Position"
                  className="w-full h-48 object-cover"
                />
              </div>
              <p className="text-sm text-gray-700 mt-2 font-medium">Step 1: Hand Placement</p>
              <p className="text-xs text-gray-600 mt-1">Place the heel of one hand on the center of the chest (lower half of breastbone). Place your other hand on top and interlock fingers.</p>
            </div>

            {/* Image 2: Hand Position */}
            <div className="mb-4">
              <div className="bg-blue-50 rounded-xl overflow-hidden">
                <img
                  src="/Hand_positioning.png"
                  alt="CPR Hand Position"
                  className="w-full h-48 object-cover"
                />
              </div>
              <p className="text-sm text-gray-700 mt-2 font-medium">Step 2: Body Position</p>
              <p className="text-xs text-gray-600 mt-1">Position shoulders directly over hands. Keep elbows locked and arms straight.</p>
            </div>

            {/* Image 3: Compression Technique */}
            <div className="mb-4">
              <div className="bg-blue-50 rounded-xl overflow-hidden">
                <img
                  src="/CPR.png"
                  alt="CPR Compression"
                  className="w-full h-48 object-cover"
                />
              </div>
              <p className="text-sm text-gray-700 mt-2 font-medium">Step 3: Compression</p>
              <p className="text-xs text-gray-600 mt-1">Push hard and fast. Compress at least 2 inches deep at a rate of 100-120 compressions per minute.</p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
              <p className="text-sm font-semibold text-red-900">Key Points:</p>
              <ul className="text-xs text-red-800 mt-2 space-y-1">
                <li>• Allow chest to recoil completely between compressions</li>
                <li>• Minimize interruptions in compressions</li>
                <li>• Continue until help arrives or person shows signs of life</li>
              </ul>
            </div>
          </div>
        </div>
        <BottomNav currentScreen="profile" setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }
if (currentScreen === 'choking') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header title="Choking Relief" onBack={() => setCurrentScreen('profile')} />
        <div className="p-4 space-y-6 pb-24">
          <div className="bg-white rounded-2xl p-6">
            <h2 className="font-bold text-xl mb-4">Heimlich Maneuver Steps</h2>
            <div className="space-y-4">
              {[
                'Stand behind the person and wrap your arms around their waist',
                'Make a fist with one hand and place it above the navel',
                'Grasp your fist with the other hand',
                'Give quick, upward thrusts into the abdomen',
                'Repeat until the object is dislodged',
                'Call emergency services if unsuccessful'
              ].map((text, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">{i + 1}</div>
                  <p className="text-gray-700 pt-1">{text}</p>
                </div>
              ))}
            </div>
          </div>
<div className="bg-white rounded-2xl p-6">
            <h3 className="font-semibold mb-3">Heimlich Maneuver Technique</h3>
            
            {/* Image 1: Standing Position */}
            <div className="mb-4">
              <div className="bg-orange-50 rounded-xl overflow-hidden">
                <img
                  src="/Heimlich_positioning.png"
                  alt="Heimlich Standing Position"
                  className="w-full h-48 object-cover"
                />
              </div>
              <p className="text-sm text-gray-700 mt-2 font-medium">Step 1: Positioning</p>
              <p className="text-xs text-gray-600 mt-1">Stand behind the person. Wrap your arms around their waist. Position yourself slightly to one side.</p>
            </div>

            {/* Image 2: Fist Placement */}
            <div className="mb-4">
              <div className="bg-orange-50 rounded-xl overflow-hidden">
                <img
                  src="/Heimlich_hand.png"
                  alt="Heimlich Fist Position"
                  className="w-full h-48 object-cover"
                />
              </div>
              <p className="text-sm text-gray-700 mt-2 font-medium">Step 2: Hand Placement</p>
              <p className="text-xs text-gray-600 mt-1">Make a fist with one hand. Place it just above the person's navel, below the ribcage. Grasp your fist with your other hand.</p>
            </div>

            {/* Image 3: Thrust Motion */}
            <div className="mb-4">
              <div className="bg-orange-50 rounded-xl overflow-hidden">
                <img
                  src="/Abdominal_thrust.png"
                  alt="Heimlich Thrust"
                  className="w-full h-48 object-cover"
                />
              </div>
              <p className="text-sm text-gray-700 mt-2 font-medium">Step 3: Abdominal Thrusts</p>
              <p className="text-xs text-gray-600 mt-1">Give quick, upward thrusts into the abdomen. Use enough force to dislodge the object. Repeat until object is expelled.</p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
              <p className="text-sm font-semibold text-yellow-900">Important Notes:</p>
              <ul className="text-xs text-yellow-800 mt-2 space-y-1">
                <li>• For adults and children over 1 year old</li>
                <li>• For infants, use back blows and chest thrusts</li>
                <li>• If person becomes unconscious, begin CPR</li>
                <li>• Seek medical attention even if object is dislodged</li>
              </ul>
            </div>
          </div>
        </div>
        <BottomNav currentScreen="profile" setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }

  if (currentScreen === 'bleeding') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header title="Bleeding Control" onBack={() => setCurrentScreen('profile')} />
        <div className="p-4 space-y-6 pb-24">
          <div className="bg-white rounded-2xl p-6">
            <h2 className="font-bold text-xl mb-4">Severe Bleeding Control Steps</h2>
            <div className="space-y-4">
              {[
                'Ensure scene safety and use protective gloves if available',
                'Apply direct pressure to the wound with a clean cloth',
                'Maintain firm, continuous pressure for at least 10 minutes',
                'If blood soaks through, add more cloth without removing the first',
                'Elevate the injured area above the heart if possible',
                'Apply pressure to arterial pressure points if bleeding continues',
                'Call emergency services immediately for severe bleeding'
              ].map((text, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">{i + 1}</div>
                  <p className="text-gray-700 pt-1">{text}</p>
                </div>
              ))}
            </div>
          </div>
<div className="bg-white rounded-2xl p-6">
            <h3 className="font-semibold mb-3">Bleeding Control Techniques</h3>
            
            {/* Image 1: Direct Pressure with hand */}
            <div className="mb-4">
              <div className="bg-red-50 rounded-xl overflow-hidden">
                <img
                  src="/pressure_hands.png"
                  alt="Direct Pressure on Wound"
                  className="w-full h-48 object-cover"
                />
              </div>
              <p className="text-sm text-gray-700 mt-2 font-medium">Step 1: Direct Pressure</p>
              <p className="text-xs text-gray-600 mt-1">Apply firm, direct pressure to the wound using a clean cloth or sterile gauze. Maintain continuous pressure for at least 10 minutes.</p>
            </div>

            {/* Image 2: Dressing and Elevation */}
            <div className="mb-4">
              <div className="bg-red-50 rounded-xl overflow-hidden">
                <img
                  src="/bleed_press.png"
                  alt="Elevate Injured Area"
                  className="w-full h-48 object-cover"
                />
              </div>
              <p className="text-sm text-gray-700 mt-2 font-medium">Step 2: Elevation</p>
              <p className="text-xs text-gray-600 mt-1">If possible, elevate the injured area above the level of the heart to help slow bleeding.</p>
            </div>

            {/* Image 3: Pressure Points- use tourniquet */}
            <div className="mb-4">
              <div className="bg-red-50 rounded-xl overflow-hidden">
                <img
                  src="/Tourniquet.png"
                  alt="Arterial Pressure Points"
                  className="w-full h-48 object-cover"
                />
              </div>
              <p className="text-sm text-gray-700 mt-2 font-medium">Step 3: Pressure Points (if needed)</p>
              <p className="text-xs text-gray-600 mt-1">If bleeding continues, apply pressure to arterial pressure points between wound and heart. Common points: brachial artery (arm) and femoral artery (leg).</p>
            </div>

            <div className="bg-red-100 border border-red-300 rounded-lg p-3 mt-4">
              <p className="text-sm font-semibold text-red-900">⚠️ Critical Guidelines:</p>
              <ul className="text-xs text-red-800 mt-2 space-y-1">
                <li>• Never remove embedded objects - stabilize them</li>
                <li>• Add more cloth if blood soaks through, don't remove first layer</li>
                <li>• Call emergency services immediately for severe bleeding</li>
                <li>• Watch for signs of shock (pale, cold, rapid pulse)</li>
              </ul>
            </div>
          </div>          
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
            <h3 className="font-bold text-yellow-900 mb-2">⚠️ Warning Signs</h3>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Spurting or pulsating blood</li>
              <li>• Blood soaking through multiple layers</li>
              <li>• Signs of shock (pale, cold, rapid pulse)</li>
              <li>• Loss of consciousness</li>
            </ul>
          </div>
        </div>
        <BottomNav currentScreen="profile" setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }
  if (currentScreen === 'profile') {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header title="" />
        <div className="p-4 pb-24">
          <div className="mb-6">
            <h3 className="font-bold text-lg mb-3 px-2">User</h3>
            <div className="bg-white rounded-2xl overflow-hidden">
              {[
                { label: 'Activity History', screen: 'activityHistory' },
                { label: 'Event Codes', screen: 'eventCodes' },
                { label: 'Feedback', screen: 'feedback' },
              ].map((item, i) => (
                <button key={i} onClick={() => item.screen && setCurrentScreen(item.screen)} className="w-full px-4 py-4 flex justify-between items-center border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
                  <span className="text-gray-700">{item.label}</span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>
          </div>
          <div className="mb-6">
            <h3 className="font-bold text-lg mb-3 px-2">Settings</h3>
            <div className="bg-white rounded-2xl overflow-hidden">
              {[
                { label: 'Notification', screen: 'notifications' },
                { label: 'Account Management', screen: null },
                { label: 'Legal and Policies', screen: null }
              ].map((item, i) => (
                <button key={i} onClick={() => item.screen && setCurrentScreen(item.screen)} className="w-full px-4 py-4 flex justify-between items-center border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
                  <span className="text-gray-700">{item.label}</span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>
          </div>
<div className="mb-6">
            <h3 className="font-bold text-lg mb-3 px-2">Emergency Procedures</h3>
            <div className="bg-white rounded-2xl overflow-hidden">
              {[
                { id: 'cpr', label: 'CPR for Adults', screen: 'cpr' },
                { id: 'choking', label: 'Choking Relief - Heimlich Maneuver', screen: 'choking' },
                { id: 'bleeding', label: 'Severe Bleeding Control', screen: 'bleeding' }
              ].map((procedure, i) => (
                <button 
                  key={i} 
                  onClick={() => setCurrentScreen(procedure.screen)} 
                  className="w-full px-4 py-4 flex justify-between items-center border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                >
                  <span className="text-gray-700">{procedure.label}</span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>
          </div>
        </div>
        <BottomNav currentScreen={currentScreen} setCurrentScreen={setCurrentScreen} />
      </div>
    );
  }

  return null;
};
const Header = ({ title, onBack, showBack = true }) => (
  <div className="bg-white px-4 py-3 border-b sticky top-0 z-10">
    <div className="flex items-center justify-between">
      {showBack && onBack ? (
        <button onClick={onBack} className="text-blue-500 flex items-center gap-1">
          <ChevronLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
      ) : (
        <span className="text-sm">{new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
      )}
      <span className="font-semibold">{title}</span>
      <div className="w-12"></div>
    </div>
  </div>
);
const NavigationPanel = ({ steps, currentIndex, onClose }) => {
  if (!steps || steps.length === 0) return null;

  const currentStep = steps[currentIndex];
  const nextStep = steps[currentIndex + 1];
  
  const getManeuverIcon = (maneuver) => {
    switch(maneuver) {
      case 'turn-right': return '➡️';
      case 'turn-left': return '⬅️';
      case 'turn-slight-right': return '↗️';
      case 'turn-slight-left': return '↖️';
      case 'uturn': return '🔄';
      default: return '⬆️';
    }
  };

  return (
    <div className="fixed bottom-24 right-4 bg-white rounded-2xl shadow-2xl p-4 w-80 z-[1001]">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold text-lg">Turn-by-Turn</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          ✕
        </button>
      </div>

      {/* Current Step */}
      <div className="bg-blue-50 rounded-xl p-4 mb-3">
        <div className="flex items-start gap-3">
          <span className="text-4xl">{getManeuverIcon(currentStep.maneuver)}</span>
          <div className="flex-1">
            <p className="font-bold text-blue-900 mb-1">{currentStep.instruction}</p>
            <p className="text-sm text-blue-700">in {currentStep.distance}</p>
          </div>
        </div>
      </div>

      {/* Next Step Preview */}
      {nextStep && (
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-600 mb-1">Then:</p>
          <div className="flex items-center gap-2">
            <span className="text-xl">{getManeuverIcon(nextStep.maneuver)}</span>
            <p className="text-sm text-gray-700">{nextStep.instruction}</p>
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="mt-3 pt-3 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Step {currentIndex + 1} of {steps.length}
        </p>
        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all"
            style={{ width: `${((currentIndex + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const BottomNav = ({ currentScreen, setCurrentScreen }) => (
  <div className="fixed bottom-0 left-0 right-0 border-t border-gray-200 px-4 py-3 flex justify-between items-center z-10" style={{ backgroundColor: '#F08787' }}>
    <button onClick={() => setCurrentScreen('home')} className={`flex flex-col items-center ${currentScreen === 'home' ? 'text-blue-300' : 'text-white'}`}>
      <Home className="w-6 h-6" />
    </button>
    <button onClick={() => setCurrentScreen('createdEvents')} className={`flex flex-col items-center ${currentScreen === 'createdEvents' || currentScreen === 'eventDetail' ? 'text-blue-500' : 'text-gray-400'}`}>
      <AlertCircle className="w-6 h-6" />
    </button>
    <button onClick={() => setCurrentScreen('createEvent')} className={`flex flex-col items-center ${currentScreen === 'createEvent' ? 'text-blue-500' : 'text-gray-400'}`}>
      <PlusCircle className="w-6 h-6" />
    </button>
    <button onClick={() => setCurrentScreen('map')} className={`flex flex-col items-center ${currentScreen === 'map' || currentScreen === 'navigation' ? 'text-blue-500' : 'text-gray-400'}`}>
      <Menu className="w-6 h-6" />
    </button>
    <button onClick={() => setCurrentScreen('profile')} className={`flex flex-col items-center ${currentScreen === 'profile' || currentScreen === 'cpr' || currentScreen === 'choking' || currentScreen === 'bleeding' || currentScreen === 'activityHistory' || currentScreen === 'eventCodes' || currentScreen === 'feedback' || currentScreen === 'notifications' ? 'text-blue-500' : 'text-gray-400'}`}>
      <User className="w-6 h-6" />
    </button>
  </div>
);

const RouteDisplay = ({ start, end }) => {
  const [route, setRoute] = useState([]);
  const map = useMap();

  useEffect(() => {
    if (!start || !end) return;

    const getRoute = async () => {
      const coords = await fetchRoute(start[0], start[1], end[0], end[1]);
      setRoute(coords);

      if (coords.length > 2 && map) {
        const bounds = L.latLngBounds(coords);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    };

    getRoute();
  }, [start, end, map]);

  if (route.length < 2) return null;

  return <Polyline positions={route} color="#10B981" weight={5} opacity={0.8} />;
};

{/* ================ AudioBubble component ================ */}
const AudioBubble = ({ url, isMine }) => {
  const audioRefLocal = React.useRef(null);
  const [isPlayingLocal, setIsPlayingLocal] = React.useState(false);
  const [durationLocal, setDurationLocal] = React.useState(null);

  React.useEffect(() => {
    const a = audioRefLocal.current;
    if (!a) return;
    const onLoaded = () => setDurationLocal(a.duration);
    const onEnded = () => setIsPlayingLocal(false);
    a.addEventListener('loadedmetadata', onLoaded);
    a.addEventListener('ended', onEnded);
    return () => {
      a.removeEventListener('loadedmetadata', onLoaded);
      a.removeEventListener('ended', onEnded);
    };
  }, [url]);

  const togglePlayLocal = () => {
    const a = audioRefLocal.current;
    if (!a) return;
    if (isPlayingLocal) { a.pause(); setIsPlayingLocal(false); }
    else { a.play().catch(e => console.warn('play failed', e)); setIsPlayingLocal(true); }
  };

  const timeLabel = durationLocal ? `${Math.floor(durationLocal)}s` : '...';

  return (
    <div className={`p-2 rounded-lg inline-flex items-center gap-3 ${isMine ? 'bg-blue-500 text-white' : 'bg-gray-700 text-white'}`}>
      <button onClick={togglePlayLocal} className="w-9 h-9 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
        {isPlayingLocal ? '⏸' : '▶'}
      </button>
      <div className="text-sm opacity-90">{timeLabel}</div>
      <audio ref={audioRefLocal} src={url} className="hidden" />
    </div>
  );
};

export default App;



